import {
  DocumentInfo, EngagementStepStatus,
  EngagementStepType,
  EngagementTask, EngagementTaskStatus, EngagementTaskType, isTaskCompleted, SignatureRequirementType
} from '@aiwyn/api';
import { AuthService } from '@aiwyn/auth';
import { LaunchDarklyFlag, LaunchDarklyState } from '@aiwyn/config';
import {
  AnnotationChangedAction,
  AnnotationChangedEvent,
  AnnotationSubject,
  applicationDebounceTime,
  Field,
  generateUniqueCode,
  getAnnotationFieldName,
  getInitials,
  ignoreFalsy,
  LoadableStatus,
  Logger,
  RectCoordinates,
  TrackService,
  ViewAndSignPdfComponent,
} from '@aiwyn/shared';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, input, OnDestroy, OnInit, QueryList } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { BehaviorSubject, EMPTY, from, merge, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, delay, filter, finalize, map, shareReplay, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import {
  ConsentDocumentStep,
  ConsentValues,
  DocumentSignedBySomeoneElseStrategyModel,
  DocumentUnderRevisionStrategyModel,
  ENGAGEMENT_STEP_TASKS,
  EngagementAlreadyExecutedStrategyModel,
  EngagementConsentRequiredStrategyModel,
  EngagementDeletedStrategyModel,
  EngagementDocumentAlteredBeforeSigningStrategyModel,
  EngagementErrorDialogDataModel,
  EngagementGenericErrorStrategyModel,
  EngagementLetterStep,
  EngagementStepComponent,
  EngagementStepSubmissionResult,
  FooterButtonType,
  STEP_LABEL_DOCUMENT_TYPE_MAPPING,
  UserNoLongerSignatoryStrategyModel,
} from '../../models';
import {
  ConfirmEngagementViewed,
  EngagementsState,
  GetEngagementConsentSettings,
  GetEngagementDocument,
  SetActiveSigningContact,
  UpdateSelectedDocument,
} from '../../state';
import { ConfirmationModalComponent } from '../../../../shared/containers/dialog/confirmation/confirmation-modal.component';
import { EngagementErrorDialogComponent } from '../engagement-error-dialog/engagement-error-dialog.component';
import { ProvideOrDenyConsentDialogComponent } from '../provide-or-deny-consent-dialog/provide-or-deny-consent-dialog.component';
import {
  AnnotationType,
  DocumentFieldType,
  escapeHtml,
  findFieldByName,
  getAnnotationDocumentContactId,
  getAnnotationFieldType,
  getAnnotationSignatureBlockNumber,
  getContactManagerHTML,
  getFieldType,
  getSignatureBlockNumber,
  NavigableAnnotation,
  RadioAnnotation,
  SignatureAnnotation,
  TextAnnotation,
  verifyOrUpdateConsentRadioButtonPositions,
  getSignButtonHTML,
  getInitialsButtonHTML,
  getLegacySignHereButtonHTML,
} from './apryse-utils';

import WidgetAnnotation = Core.Annotations.WidgetAnnotation;
import FreeHandAnnotation = Core.Annotations.FreeHandAnnotation;
import StampAnnotation = Core.Annotations.StampAnnotation;
import Annotation = Core.Annotations.Annotation;

interface EngagementErrorStrategyPayload {
  engagementId: number;
  errorCode: string;
  errorMessage: string;
}
/**
 * Abstract class for all steps in the engagement process that require the user to interact with a PDF document. New steps will need to be
 * included on the @input 'details' union type.
 *
 *
 * IMPLEMENTATION NOTES
 *
 * The child component must implement and decorate the following properties:
 * - details: @Input()
 * - pdfViewer: @ViewChildren()
 * For an example, see the EngagementLetterStepComponent.
 *
 * The child component must point to the absrract-apryse-step template and styles in the @Component decorator
 */
@Component({
  template: '',
  standalone: true,
})
export abstract class AbstractApryseStep implements EngagementStepComponent, OnInit, AfterViewInit, OnDestroy {
  FIELD_NAVIGATION_BUTTONS = [FooterButtonType.PREVIOUS_FIELD, FooterButtonType.NEXT_FIELD];
  ASSOCIATED_SIGNATURE_ID = 'associatedSignatureId';

  // Cache of drawn signatures/initials for multi-identity signing.
  // Keyed by "contactId#positionInGroup" (see cacheKeyForDocContactId) so that:
  // - Multiple slots sharing the same contactId (same person signing 3 times) keep separate sigs
  // - The same position persists across documents (letter → consent) for cross-step restore
  // Static so it persists across step components.
  private static _savedSignaturesPerContact = new Map<string, { signatures: any[]; initials: any[] }>();

  // Identity of the auth-provider user that last touched the singleton WebViewer's
  // signature tool. Used to detect login changes (e.g., secure-link signer A → signer B
  // on the same tab) and wipe Apryse's internal saved signatures + the per-contact cache
  // before the new user can see another contact's drawn signature in the modal.
  private static _lastSignatureToolUserId: string | undefined;

  // Engagement id the cache was last populated for. The cache is meant to persist between
  // documents within the same engagement (letter → consent → addon) but NOT across
  // engagements — signatures drawn on engagement A should not surface in engagement B's
  // signature modal. Compared in onInstanceInitialization and the cache is cleared when
  // the engagement id changes.
  private static _lastSignatureToolEngagementId: number | undefined;

  // NOTE: Must be overridden in children with correct @Input decorator
  details = input<EngagementLetterStep | ConsentDocumentStep>(undefined);
  status: EngagementStepStatus;
  
  isMobile$: Observable<boolean> = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(
      map(breakpointMatched => breakpointMatched.matches),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  private readonly _unsubscribe$ = new Subject<void>();
  private readonly _instanceEventHandlers: Array<{ target: any; event: string; handler: (...args: any[]) => void }> = [];

  // NOTE: Must be overridden in children with correct @ViewChildren decorator
  pdfViewer: QueryList<ViewAndSignPdfComponent>;

  pdfViewerInstance$ = new BehaviorSubject<WebViewerInstance>(undefined);
  navigableAnnotations: NavigableAnnotation[] = [];
  documentLoadable$ = of({ status: LoadableStatus.DEFAULT });
  footerButtonsSubject$ = new Subject<FooterButtonType[]>();

  onKeyUp$ = new Subject<void>();
  onSignatureAdded$ = new Subject<string>();
  saveDocumentChanges$ = merge(
    this.onKeyUp$.asObservable().pipe(
      applicationDebounceTime(),
      map(() => this.getOutOfDateFields()),
      // stop if no changes were made (keyUp event occurred outside editable text annotation)
      filter(({ length }) => length > 0),
      tap(outOfDateFields => this.updateOutOfDateFieldValues(outOfDateFields)),
    ),
    this.onSignatureAdded$.asObservable().pipe(
      applicationDebounceTime(), // Fix: Debounce signature saves to prevent race condition with Apryse annotation commit
    ),
  ).pipe(
    switchMap(res => this.pdfViewer.first.getDocumentWithAnnotationData().pipe(
      switchMap(selectedDocumentData => this.store.dispatch(new UpdateSelectedDocument(
        selectedDocumentData,
        this.details().type,
        typeof res === 'string' && res
      ))),
    )),
    takeUntil(this._unsubscribe$),
  );
  protected _fieldValues$ = new BehaviorSubject<Record<string, string | number>>({});
  private _isSubmitting = false;

  currentFieldIndex: number | null = null;

  protected readonly trackService = inject(TrackService);

  constructor(
    readonly store: Store,
    readonly changeDetectorRef: ChangeDetectorRef,
    readonly datePipe: DatePipe,
    readonly dialog: MatDialog,
    readonly breakpointObserver: BreakpointObserver,
    readonly authService: AuthService,
    readonly logger: Logger,
  ) { }

  /**
   * Generates a unique alphanumeric code
   */
  ngOnInit(): void {
    const documentInfo = this.getCurrentDocumentInfo();

    this.store.dispatch(new GetEngagementDocument(documentInfo.file.id, this.details().type));

    if (this.details().type === EngagementStepType.LETTER) {
      this.store.select(EngagementsState.getSelectedEngagementReportStatus).pipe(
        withLatestFrom(this.store.select(EngagementsState.currentUserHasViewedSelectedEngagement)),
        filter(([status, currentUserHasViewedEngagement]) => status === LoadableStatus.LOADED && !currentUserHasViewedEngagement),
        tap(() => this.store.dispatch(new ConfirmEngagementViewed())),
        take(1),
        takeUntil(this._unsubscribe$),
      ).subscribe();
    }

    if ([EngagementStepType.CONSENT, EngagementStepType.ADD_ON].includes(this.details().type)) {
      this.store.dispatch(new GetEngagementConsentSettings(documentInfo.documentId));
    }

    this.documentLoadable$ = this.store.select(EngagementsState.getSelectedEngagementDocumentLoadable(documentInfo.file.id));

    // When multi-identity data finishes loading (async, may arrive after annotations are rendered),
    // re-evaluate annotations so all contacts' fields become visible.
    // Wait for BOTH multi-identity state AND the WebViewer instance to be ready.
    // On later steps (e.g., consent after letter) multi-identity is already loaded when ngOnInit
    // runs, but the recycled instance hasn't been acquired yet. Without waiting for the instance,
    // re-evaluation would be silently skipped.
    this.store.select(EngagementsState.isMultipleIdentitySigningActive).pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.pdfViewerInstance$.pipe(filter(Boolean), take(1))),
      takeUntil(this._unsubscribe$),
    ).subscribe(() => {
      this.reEvaluateAnnotationsForActiveContact();
      
      // Re-sync the recycled tool so cross-step leftovers can't get applied on the next click.
      this.syncSignatureToolToContact(this.getCurrentUserDocumentContactId());
    });
  }

  ngAfterViewInit(): void {
    if (!this.pdfViewer) {
      console.warn('OI! DEVELOPER! You forgot to add the decorator to the pdf viewer property in your component!');
    }
  }

  getFooterButtons(): Observable<FooterButtonType[]> {
    return this.pdfViewerInstance$.asObservable().pipe(
      filter<WebViewerInstance>(Boolean),
      map(({ Core }) => Core.documentViewer),
      tap(documentViewer => {
        const handler = () => this.footerButtonsSubject$.next(this.navigableAnnotations?.length > 0 ? this.FIELD_NAVIGATION_BUTTONS : []);
        this._addInstanceEventHandler(documentViewer, 'annotationsLoaded', handler);
      }),
      switchMap(_ => this.footerButtonsSubject$.asObservable()),
      take(1),
      takeUntil(this._unsubscribe$),
    );
  }

  async onInstanceInitialization(instance: WebViewerInstance): Promise<void> {
    // The WebViewer instance is preloaded once per tab and recycled across engagements
    // and logins (see WebViewerPreloadService). Apryse's signatureTool keeps drawn
    // signatures in-memory on that singleton, so without an explicit purge a previous
    // signer's signature would be offered to the next user that opens the modal. The
    // engagement check additionally scopes the per-contact cache to a single engagement
    // — same contact across letter / consent / addon keeps the cached drawing, but
    // jumping from engagement A to engagement B starts fresh.
    this.clearSignatureCachesIfUserChanged(instance);
    this.clearSignatureCachesIfEngagementChanged(instance);

    // https://docs.apryse.com/documentation/web/guides/annotation/annotationmanager/permissions/#default-behavior
    // This is useful, particularly, in updating what value appears in the type option in the signatureCreateTool
    // In this case it will use the value set currently for the logged-in user (contact)
    // This also gets exported to the xfdf, so also useful to keep track of the history.
    instance.Core.annotationManager.setCurrentUser(this.authService.getUserSnapshot()?.getDisplayName());

    // The WebViewer instance is recycled across steps. The previous step's submission calls
    // annotationManager.enableReadOnlyMode(), which persists on the recycled instance.
    // Undo it here so the next step's annotations are interactive.
    if (!this.details().isReadonly) {
      instance.Core.annotationManager.disableReadOnlyMode();
    }

    this.pdfViewerInstance$.next(instance);
    this.saveDocumentChanges$.subscribe();

    const signatureTool = instance.Core.documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;

    // WebViewer 11 Fix: Set the signing mode to ANNOTATION at the tool level.
    // This prevents signatures from being committed as widget appearances (which causes them to visually disappear).
    // The ANNOTATION mode keeps signatures as separate annotation objects that remain visible.
    // SigningModes can be on the tool instance or on the Core.Tools.SignatureCreateTool class
    const SigningModes = (signatureTool as any).SigningModes
      || (instance.Core.Tools as any).SignatureCreateTool?.SigningModes;
    if (SigningModes?.ANNOTATION !== undefined && typeof (signatureTool as any).setSigningMode === 'function') {
      (signatureTool as any).setSigningMode(SigningModes.ANNOTATION);
      this.logger.info('ENGAGEMENTS : AbstractApryseStep : Set signature tool signing mode to ANNOTATION');
    } else {
      this.logger.warn('ENGAGEMENTS : AbstractApryseStep : Could not set signing mode - SigningModes or setSigningMode not available');
    }

    // When a signature annotation is selected (clicked), switch the active contact to match.
    // This ensures the dropdown, cached signatures, and signing context stay in sync.
    this._addInstanceEventHandler(instance.Core.annotationManager, 'annotationSelected', (annotations: Annotation[], action: string) => {
      if (action !== 'selected') {
        return;
      }

      const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);
      if (!isMultiIdentity) {
        return;
      }

      // Find the first selected signature annotation
      const signatureAnnotation = annotations.find(annotation => annotation.Subject === AnnotationSubject.SIGNATURE);
      if (!signatureAnnotation) {
        return;
      }

      const matchingWidget = this.getMatchingSignatureWidgetAnnotation(signatureAnnotation);
      if (!matchingWidget) {
        return;
      }

      const documentContactId = getAnnotationDocumentContactId(matchingWidget);
      const currentActiveDocContactId = this.getCurrentUserDocumentContactId();

      if (documentContactId !== currentActiveDocContactId) {
        const targetContactId = this.store.selectSnapshot(EngagementsState.getContactIdByDocumentContactId(documentContactId));

        if (targetContactId) {
          this.store.dispatch(new SetActiveSigningContact(targetContactId));
          this.updateCurrentUserForActiveContact();
        }
      }
    });

    // Reset the tool to AnnotationEdit after the signature modal closes — otherwise it stays
    // armed in placement mode and every subsequent click reopens the modal. Delay lets the
    // async addSignature/addInitials from locationSelected finish first.
    this._addInstanceEventHandler(instance.UI, instance.UI.Events.VISIBILITY_CHANGED, (event: any) => {
      const { element, isVisible } = event?.detail ?? event ?? {};
      if (element !== 'signatureModal' || isVisible) {
        return;
      }

      setTimeout(() => {
        const currentTool = instance.Core.documentViewer.getToolMode();
        if (currentTool === signatureTool) {
          instance.UI.setToolMode('AnnotationEdit');
        }
      }, 300);
    });

    // https://docs.apryse.com/api/web/Core.Tools.SignatureCreateTool.html#event:locationSelected__anchor
    this._addInstanceEventHandler(signatureTool, 'locationSelected', async (_coords: any, sigWidget: Core.Annotations.SignatureWidgetAnnotation) => {
      if (!sigWidget) {
        return;
      }

      // Bail if the click landed on another contact's widget while the tool was still armed —
      // otherwise we'd stamp the previous contact's saved sig onto the new widget.
      const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);

      if (isMultiIdentity) {
        const widgetDocContactId = getAnnotationDocumentContactId(sigWidget);
        const activeDocContactId = this.getCurrentUserDocumentContactId();

        if (widgetDocContactId !== activeDocContactId) {
          instance.UI.setToolMode('AnnotationEdit');
          return;
        }
      }

      // Prefer owner-tagged entries; fall back to the last entry for the fresh-draw case.
      const widgetDocContactIdStr = String(getAnnotationDocumentContactId(sigWidget));
      const savedInitials = signatureTool.getSavedInitials();
      const savedSignatures = signatureTool.getSavedSignatures();
      
      const findOwned = (list: any[]) => [...list].reverse().find(annotation => annotation?.getCustomData?.(AbstractApryseStep.OWNER_CONTACT_KEY) === widgetDocContactIdStr);
      
      const savedInitial = findOwned(savedInitials) ?? savedInitials[savedInitials.length - 1];
      const savedSignature = findOwned(savedSignatures) ?? savedSignatures[savedSignatures.length - 1];

      if (!savedInitial && !savedSignature) {
        return;
      }

      if (sigWidget.requiresInitials()) {
        await signatureTool.setInitials(savedInitial);
        await signatureTool.addInitials();
      } else {
        await signatureTool.setSignature(savedSignature);
        await signatureTool.addSignature();
      }
    });
  }

  onDocumentLoaded(): void {
    this.logger.info('ENGAGEMENTS : AbstractApryseStep :: Document Loaded', { details: this.details(), info: this.getCurrentDocumentInfo() });

    this.setCustomAnnotationStyles();
    this.enableBoundingBoxSelectionForSignatures();

    this._addInstanceEventHandler(this.pdfViewerInstance$.value.Core.documentViewer, 'keyUp', () => this.onKeyUp$.next());

    this.pdfViewerInstance$.pipe(ignoreFalsy()).pipe(
      tap((instance) => {
        this._addInstanceEventHandler(instance.Core.documentViewer, 'annotationsLoaded', async () => {
          const annotations = instance.Core.annotationManager.getAnnotationsList();
          const signatureWidgetAnnotations = annotations.filter((annotation): annotation is Core.Annotations.SignatureWidgetAnnotation =>
            annotation instanceof instance.Core.Annotations.SignatureWidgetAnnotation);

          if (
            signatureWidgetAnnotations.some(annotation => getAnnotationFieldType(annotation) === DocumentFieldType.INITIAL)
          ) {
            // Enables Initials feature for WebViewer
            instance.UI.enableFeatures([instance.UI.Feature.Initials]);
          }

          // WebViewer 11 Fix: Revert to v10's annotation-based signing instead of v11's "sign by appearance"
          // This prevents typed signatures from disappearing after being committed to the widget
          signatureWidgetAnnotations.forEach(widget => {
            if (typeof (widget as any).setSignByAppearance === 'function') {
              (widget as any).setSignByAppearance(false);
            }
          });

          // Creates new annotation for initials, so that they are recognized as an actual initial block
          // because the Kotlin SDK does not distinguish between signature vs. initials when creating an annotation
          // and inside the WebViewer we are able to create these passing in the requireInitials flag.
          const annotationsToDelete = [] as Core.Annotations.WidgetAnnotation[];
          signatureWidgetAnnotations.forEach(annotation => {
            // If ASSOCIATED_SIGNATURE_ID has already been set to the initial annotation, it means it already has been signed previously.
            // In that case we don't want to remove that annotation and create a new one, losing that association.
            if (getAnnotationFieldType(annotation) === DocumentFieldType.INITIAL && !annotation.getCustomData(this.ASSOCIATED_SIGNATURE_ID)) {
              annotationsToDelete.push(annotation);
            }
          });

          const isUserTheFirstSignatory = this.store.selectSnapshot(EngagementsState.isUserTheFirstSignatory);

          // https://docs.apryse.com/documentation/web/guides/forms/create-signature-field/
          const formFieldCreationManager = instance.Core.annotationManager.getFormFieldCreationManager();
          const newInitialWidgets = annotationsToDelete.map(annotation => {
            const initialWidget = new instance.Core.Annotations.SignatureWidgetAnnotation(annotation.getField());

            initialWidget.PageNumber = annotation.PageNumber;
            initialWidget.X = annotation.getX();
            initialWidget.Y = isUserTheFirstSignatory ? annotation.getY() - (annotation.Height / 2) : annotation.getY();
            initialWidget.Width = annotation.Width;
            initialWidget.Height = annotation.Height;

            // Preserve custom data from the original annotation (e.g., ASSOCIATED_SIGNATURE_ID)
            const existingCustomData = annotation.getCustomData?.(this.ASSOCIATED_SIGNATURE_ID);
            if (existingCustomData) {
              initialWidget.setCustomData(this.ASSOCIATED_SIGNATURE_ID, existingCustomData);
            }

            // Set signature option to initials for each widget after they're added (WebViewer 11 API)
            formFieldCreationManager.setSignatureOption(initialWidget, 'initialsSignature');

            return initialWidget;
          });

          instance.Core.annotationManager.deleteAnnotations(annotationsToDelete, { force: true, source: 'duplicate' });
          instance.Core.annotationManager.addAnnotations(newInitialWidgets, { imported: true });

          // WebViewer 11 Fix: Apply setSignByAppearance(false) to newly created initial widgets
          // This must be done AFTER adding the annotations, otherwise the setting may not persist
          newInitialWidgets.forEach(widget => {
            if (typeof (widget as any).setSignByAppearance === 'function') {
              (widget as any).setSignByAppearance(false);
            }
          });

          await instance.Core.annotationManager.drawAnnotationsFromList(newInitialWidgets);

          // Consent radio normalization:
          // Some templates place a Yes/No pair across a page break. WebViewer preserves the original pageNumber even if a widget’s Y spills
          // past the page height, so both radios can be assigned to page N. This pass detects that pattern (large vertical gap on same page)
          // and moves the upper radio to page N+1. Idempotent; safe to run after `annotationsLoaded` and on `pageComplete`.
          await verifyOrUpdateConsentRadioButtonPositions(instance);

          this.logger.info('ENGAGEMENTS : AbstractApryseStep :: Annotations Loaded');
        });
      }),
      take(1),
    ).subscribe();
  }

  onDeleteSignature(): void {
    if (this.details().isReadonly) {
      return;
    }

    const instance = this.pdfViewerInstance$.value;
    const [selectedAnnotation] = instance.Core.annotationManager.getSelectedAnnotations();

    if (!selectedAnnotation || selectedAnnotation.Subject !== AnnotationSubject.SIGNATURE) {
      return;
    }

    // processSignature's 'delete' branch clears the widget appearance, custom data, and
    // the auto-populated date/name fields.
    instance.Core.annotationManager.deleteAnnotations([selectedAnnotation], { force: true });
  }

  onEditSignature(): void {
    // Don't allow editing signatures in readonly mode
    if (this.details().isReadonly) {
      return;
    }

    const instance = this.pdfViewerInstance$.value;
    const [selectedAnnotation] = instance.Core.annotationManager.getSelectedAnnotations();
    const widgetAnnotation = this.getMatchingSignatureWidgetAnnotation(selectedAnnotation) as Core.Annotations.SignatureWidgetAnnotation;
    const signatureTool = instance.Core.documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;

    // Switch active contact if the selected signature belongs to a different contact.
    // Without this, the cached signature/initials and signing context would be from the wrong contact.
    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);

    if (isMultiIdentity) {
      const documentContactId = getAnnotationDocumentContactId(widgetAnnotation);
      const currentActiveDocContactId = this.getCurrentUserDocumentContactId();

      if (documentContactId !== currentActiveDocContactId) {
        const targetContactId = this.store.selectSnapshot(EngagementsState.getContactIdByDocumentContactId(documentContactId));

        if (targetContactId) {
          this.store.dispatch(new SetActiveSigningContact(targetContactId));
          this.updateCurrentUserForActiveContact();
          // switchSavedSignatures handled by view-engagement-container's reactive subscription.
        }
      }
    }

    // The user is re-drawing their signature, so all signatures and initials for this
    // contact must be removed — they were drawn with the old signature and need to be re-done.
    const documentContactId = getAnnotationDocumentContactId(widgetAnnotation);
    const allAnnotations = instance.Core.annotationManager.getAnnotationsList();

    // Find all signature/initial annotations belonging to this contact
    const contactAnnotationsToDelete = allAnnotations.filter(annotation => {
      if (annotation.Subject !== AnnotationSubject.SIGNATURE) {
        return false;
      }

      const matchingWidget = this.getMatchingSignatureWidgetAnnotation(annotation);
      return matchingWidget && getAnnotationDocumentContactId(matchingWidget) === documentContactId;
    });

    // Clear appearances on all affected widgets before deleting
    for (const annotation of contactAnnotationsToDelete) {
      const widget = this.getMatchingSignatureWidgetAnnotation(annotation) as Core.Annotations.SignatureWidgetAnnotation;
      if (widget) {
        this.tryClearWidgetSignature(widget, instance.Core.annotationManager);
      }
    }

    // Delete all signature/initial annotations for this contact
    instance.Core.annotationManager.deleteAnnotations(contactAnnotationsToDelete, { force: true });

    // Clear the signature tool and the per-contact cache so the modal shows fresh
    while (signatureTool.getSavedSignatures?.()?.length > 0) {
      signatureTool.deleteSavedSignature(0);
    }
    while (signatureTool.getSavedInitials?.()?.length > 0) {
      signatureTool.deleteSavedInitials(0);
    }

    AbstractApryseStep._savedSignaturesPerContact.delete(this.cacheKeyForDocContactId(documentContactId));

    // Delay opening the modal to let Apryse finish processing the deletion.
    // Without this, the deletion disrupts focus and the modal won't appear.
    // Tool-reset-on-modal-close is handled globally in onInstanceInitialization.
    setTimeout(() => instance.UI.signSignatureWidget(widgetAnnotation), 0);
  }

  onAnnotationChanged([annotations, action, info]: AnnotationChangedEvent): void {
    if (this.details().isReadonly) {
      this.setReadonlyView(annotations);
      this.changeDetectorRef.detectChanges();
      return;
    }

    // this will be triggered once for every page that has annotations (each page calls it separately)
    if (info.imported && action === 'add') {
      this.hideIrrelevantAnnotations(annotations);
      this.resizeEditableSignatureAnnotations(annotations);
      this.adjustTextFieldPositions(annotations);
      this.setTypeFieldsReadonly(annotations, [DocumentFieldType.DATE, DocumentFieldType.NAME_AND_TITLE]);
      this.navigableAnnotations = this.getNavigableAnnotations(annotations);

      // selectively unlock fields based on the current step and the annotations
      if (!this.details().isReadonly) {
        this.setTypeFieldsReadonly(
          // Only touch the ones you can play with. They will not be navigable if the step has already been completed.
          this.navigableAnnotations.map(navigableAnnotation => navigableAnnotation.data),
          // Only unlock interactable fields
          [DocumentFieldType.CONSENT, DocumentFieldType.SIGNATURE],
          false
        );
      }

      if (this.navigableAnnotations.length > 0) {
        this.footerButtonsSubject$.next(this.FIELD_NAVIGATION_BUTTONS);
      }
    }

    // Handle removal of annotations and update the navigableAnnotations accordingly.
    if (action === 'delete') {
      this.navigableAnnotations = this.navigableAnnotations.filter(navigableAnnotation => !annotations.includes(navigableAnnotation.data));
    }

    const signatures = annotations.filter(({ Subject }) => Subject === AnnotationSubject.SIGNATURE) as (FreeHandAnnotation | StampAnnotation)[];

    for (const signature of signatures) {
      this.processSignature(signature, action, info.imported);
    }

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Find annotations in modified signature block, autopopulate appropriate fields, and update metadata.
   * @param signatureAnnotation rendered signature, not the 'Sign here' placeholder annotation
   * @param action 'add' | 'delete'
   * @param imported false if user just added signature, true if already part of document on load
   */
  processSignature(signatureAnnotation: Annotation, action: AnnotationChangedAction, imported: boolean): void {
    const matchingWidgetAnnotation = this.getMatchingSignatureWidgetAnnotation(signatureAnnotation);

    // CustomData is being set, because it is preserved whenever another user, or the same user, opens the document again
    // be it for signing or just viewing the document. During implementation, it was found that
    // Core.Annotations.SignatureWidgetAnnotation.getAssociatedSignatureAnnotation was not being preserved and returning null.
    // It works fine and has that link during the signing process itself, but when another user opens the document it loses that reference
    // This guarantees that it will always be present, because it gets added to the XFDF as well:
    // https://docs.apryse.com/api/web/Core.Annotations.SignatureWidgetAnnotation.html#setCustomData__anchor. Once an annotation has become readonly,
    // it is not possible to edit/set it's customData anymore. The link might be lost because the association is done by position
    // as per https://community.apryse.com/t/two-signatures-are-showing/4894/4, so the setCustomData strategy is something recommended
    // by the Apryse team. Differently from the post we do not move the signature, but since we do some modifications for the position
    // of annotations and resizing, that might be what affects it.
    if (!imported && action === 'add') {
      this.logger.info('AbstractApryseStep :: Signature Added', { annotationId: signatureAnnotation.Id, datetime: Date.now() });
      matchingWidgetAnnotation.setCustomData(this.ASSOCIATED_SIGNATURE_ID, signatureAnnotation.Id);

      // Keep the per-contact cache fresh so the next sync doesn't wipe a just-drawn signature.
      const widgetDocContactId = getAnnotationDocumentContactId(matchingWidgetAnnotation);
      if (widgetDocContactId != null) {
        this.persistToolStateToContactCache(widgetDocContactId);
      }

      // Hide the "Sign Here" red button on the widget that was just signed.
      // We stored a direct reference to the element on the widget in setCustomCreateSignHereElementHandler.
      const signHereElement = (matchingWidgetAnnotation as any)?._signHereElement as HTMLElement;
      
      if (signHereElement) {
        signHereElement.style.display = 'none';
      }

      // Delay the auto-save to allow WebViewer to finish processing the signature internally
      // This prevents race conditions where the save interferes with signature rendering
      setTimeout(() => {
        this.onSignatureAdded$.next(signatureAnnotation.Id);
      }, 200);
    }

    // WebViewer 11 Fix: In "sign by appearance" mode, when a signature is added:
    // 1. An 'add' event fires for the temporary signature annotation (FreeHand or Stamp)
    // 2. The signature is committed to the widget's appearance
    // 3. A 'delete' event fires as the temporary annotation is removed
    // For typed signatures (StampAnnotation), we need to force a page refresh after delete.
    if (action === 'add') {
      this.handleAddedSignatureAnnotation(signatureAnnotation);
    }

    // Handle signature deletion: clear custom data, appearance, and update validation
    if (action === 'delete' && !imported) {
      const instance = this.pdfViewerInstance$.value;
      if (instance && matchingWidgetAnnotation) {
        // Cast to SignatureWidgetAnnotation to access signature-specific methods
        const sigWidget = matchingWidgetAnnotation as Core.Annotations.SignatureWidgetAnnotation;

        // Clear the custom data that links the signature to the widget
        // This ensures the widget is properly detected as unsigned
        try {
          // Try to clear custom data - use empty string as some Apryse versions may not accept null
          sigWidget.setCustomData(this.ASSOCIATED_SIGNATURE_ID, '');
        } catch (error) {
          // Widget might be readonly or in a state where custom data can't be modified
          this.logger.warn('Could not clear signature custom data', error);
        }

        // CRITICAL: Clear the widget's appearance for typed signatures
        // In WebViewer 11, typed signatures (StampAnnotation) can be committed to the widget's appearance
        // even when setSignByAppearance(false) is set. We must explicitly clear the appearance.
        try {
          // Clear signature from widget - this removes both the annotation link and the appearance
          sigWidget.clearSignature(instance.Core.annotationManager);
        } catch (error) {
          // If clearSignature fails, try to manually clear appearances using the annotation manager
          try {
            if (sigWidget.appearances && Object.keys(sigWidget.appearances).length > 0) {
              // Try to clear appearances by calling clearSignature again with force
              // or by removing the appearance dictionary
              (sigWidget as any).appearances = {};
            }
          } catch (appearanceError) {
            this.logger.warn('Could not clear signature appearance', appearanceError);
          }
        }

        // WebViewer 11 Fix: When signature is deleted in "sign by appearance" mode,
        // the visual may disappear even though it was committed to the widget's appearance.
        // Force a redraw of the affected page to re-render the widget without its appearance.
        const pageNumber = matchingWidgetAnnotation.getPageNumber();
        // Use setTimeout to allow WebViewer to finish its internal processing
        setTimeout(() => {
          instance.Core.annotationManager.drawAnnotations({ pageNumber });
          // Force validation update after redraw to ensure isSigned is updated
          this.updateNavigableFieldsValidity();
        }, 100);

        // Re-show the "Sign Here" element so the user can sign again
        const signHereElement = (matchingWidgetAnnotation as any)?._signHereElement as HTMLElement;

        if (signHereElement) {
          signHereElement.style.display = '';
        }
      }
    }

    // Handle signature block field population/clearing
    if (!imported && DocumentFieldType.SIGNATURE === getAnnotationFieldType(matchingWidgetAnnotation)) {
      this.populateSignatureBlockFields(matchingWidgetAnnotation, action);
    }

    // update navigable annotations' metadata (whether signature annotation is signed and auto-populate field is populated)
    this.updateNavigableFieldsValidity();
  }

  private handleAddedSignatureAnnotation(signatureAnnotation: Annotation): void {
    // IsAdded is true when a new signature is being added. It's false when an existing signature is rendered.
    // The addedIds check for signatures that have been added to the document and, for some reason, user moved to a
    // different step, and came back to the document, making sure to set that annotation as editable.
    const addedIds = this.store.selectSnapshot(EngagementsState.getNewlyAddedSignatureIds(this.getCurrentDocumentFileId()));
    if (signatureAnnotation.IsAdded || addedIds.includes(signatureAnnotation.Id)) {
      signatureAnnotation.disableRotationControl();
      signatureAnnotation.NoResize = true;
      signatureAnnotation.NoMove = true;
    } else {
      signatureAnnotation.ReadOnly = true;
      signatureAnnotation.Listable = false;
    }
  }

  populateSignatureBlockFields(signatureAnnotation: Annotation, action: AnnotationChangedAction): void {
    const fields = this.getFields();
    const signatureBlockNumber = getAnnotationSignatureBlockNumber(signatureAnnotation);

    let matchingDateField: Field, matchingNameAndTitleField: Field;

    // find auto-populate fields in same signature block based on signature requirement type
    const documentInfo = this.getCurrentDocumentInfo();

    if (documentInfo.signatureRequirementType === SignatureRequirementType.ONE) {
      const fieldsInSameSignatureBlock = fields.filter(({ name }) => getSignatureBlockNumber(name) === signatureBlockNumber);

      matchingDateField = fieldsInSameSignatureBlock.find(({ name }) => getFieldType(name) === DocumentFieldType.DATE);
      matchingNameAndTitleField = fieldsInSameSignatureBlock
        .find(({ name }) => getFieldType(name) === DocumentFieldType.NAME_AND_TITLE);
    } else { // signatureRequirementType === SignatureRequirementType.ALL
      // Use the widget's own documentContactId (from the field name), NOT the active contact.
      // This ensures the correct date/name fields are populated regardless of which contact is active.
      const widgetDocContactId = getAnnotationDocumentContactId(signatureAnnotation);
      const baseFieldName = `Engagement.DocumentContact.${widgetDocContactId}.`;
      const dateFieldName = `${baseFieldName}${DocumentFieldType.DATE}_${signatureBlockNumber}`;
      const nameAndTitleFieldName = `${baseFieldName}${DocumentFieldType.NAME_AND_TITLE}_${signatureBlockNumber}`;

      matchingDateField = findFieldByName(fields, dateFieldName);
      matchingNameAndTitleField = findFieldByName(fields, nameAndTitleFieldName);
    }

    // Handle 'add' action: populate fields with signature data
    if (action === 'add') {
      // Set auto-populate values
      if (matchingDateField) {
        matchingDateField.setValue(this.datePipe.transform(new Date(), 'longDate'));
      }

      if (matchingNameAndTitleField && !matchingNameAndTitleField.value) {
        // For multi-identity: use the signing contact's name, not the logged-in user's name
        const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);
        const widgetDocContactId = getAnnotationDocumentContactId(signatureAnnotation);
        
        const nameAndTitleValue = isMultiIdentity
          ? this.store.selectSnapshot(EngagementsState.getContactNameByDocumentContactId(widgetDocContactId)) || ''
          : this.authService.getUserSnapshot()?.getDisplayName() || '';
        
          matchingNameAndTitleField.setValue(nameAndTitleValue);
      }
    }

    // Handle 'delete' action: clear fields when signature is removed
    // Clear name and date fields when signature is deleted (they were auto-populated when signature was added)
    if (action === 'delete') {
      // Clear the date field
      if (matchingDateField) {
        matchingDateField.setValue('');
      }

      // Clear the name and title field
      if (matchingNameAndTitleField) {
        matchingNameAndTitleField.setValue('');
      }
    }
  }

  hideIrrelevantAnnotations(annotations: Annotation[]): void {
    const isCurrentUserSignatory = this.store.selectSnapshot(EngagementsState.isUserSignatoryOnSelectedEngagement);
    const documentInfo = this.getCurrentDocumentInfo();
    const nonSignatureAnnotations = annotations.filter(annot => annot.Subject !== AnnotationSubject.SIGNATURE);

    if (documentInfo.signatureRequirementType === SignatureRequirementType.ONE && this.details().type === EngagementStepType.LETTER && isCurrentUserSignatory) {
      return;
    }

    const currentUserDocumentContactId = this.getCurrentUserDocumentContactId();
    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);
    
    const allUserDocContactIds = isMultiIdentity
      ? this.store.selectSnapshot(EngagementsState.getAllUserDocumentContactIds(documentInfo.documentId))
      : [];

    const irrelevantAnnotations = nonSignatureAnnotations.filter(annotation =>
      getAnnotationDocumentContactId(annotation) !== currentUserDocumentContactId);

    irrelevantAnnotations.forEach(irrelevantAnnotation => {
      const annotDocContactId = getAnnotationDocumentContactId(irrelevantAnnotation);
      const belongsToAnyUserContact = isMultiIdentity && allUserDocContactIds.includes(annotDocContactId);
      const isSignatureOrInitial = [DocumentFieldType.SIGNATURE, DocumentFieldType.INITIAL]
        .includes(getAnnotationFieldType(irrelevantAnnotation));

      // Non-active user contact: visible and interactive so "Sign Here" is clickable
      if (belongsToAnyUserContact) {
        irrelevantAnnotation.Listable = false;
      } else {
        // Other users' annotations: hide unsigned, make readonly
        if (!irrelevantAnnotation.IsAdded && isSignatureOrInitial) {
          const isSigned = this.isSignatureAnnotationSigned(irrelevantAnnotation);
          
          if (!isSigned) {
            irrelevantAnnotation.Hidden = true;
          }
        }

        irrelevantAnnotation.Listable = false;
        irrelevantAnnotation.ReadOnly = true;
      }
    });

    this.setCurrentUserNameAndTitleFieldsAsEditable(annotations, currentUserDocumentContactId);

    // Make irrelevant name fields and consent radios readonly
    const externalAnnotations = irrelevantAnnotations.filter(annotation =>!isMultiIdentity || !allUserDocContactIds.includes(getAnnotationDocumentContactId(annotation)));
    
    this.setTypeFieldsReadonly(externalAnnotations, [DocumentFieldType.NAME_AND_TITLE]);
    this.setTypeFieldsReadonly(externalAnnotations, [DocumentFieldType.CONSENT]);
  }

  /**
   * Check if a signature annotation is already signed
   */
  private isSignatureAnnotationSigned(annotation: Annotation): boolean {
    const instance = this.pdfViewerInstance$.value;
    if (!instance) {
      return false;
    }

    if (!(annotation instanceof instance.Core.Annotations.SignatureWidgetAnnotation)) {
      return false;
    }

    const widget = annotation as Core.Annotations.SignatureWidgetAnnotation;

    // Check if the widget has an associated signature annotation
    const hasAssociatedSignature = widget.getAssociatedSignatureAnnotation?.() != null;

    // Check if the widget has ASSOCIATED_SIGNATURE_ID custom data
    const hasCustomDataSignature = !!widget.getCustomData?.(this.ASSOCIATED_SIGNATURE_ID);

    // Check if the widget has an appearance (signature was committed/merged into widget)
    // Appearance can be under Normal, _DEFAULT, or other keys
    const hasAppearance = widget.appearances && Object.keys(widget.appearances).length > 0;

    return hasAssociatedSignature || hasCustomDataSignature || hasAppearance;
  }

  setCurrentUserNameAndTitleFieldsAsEditable(annotations: Annotation[], currentUserDocumentContactId: number): void {
    const relevantAnnotations = annotations
      .filter(annotation => getAnnotationDocumentContactId(annotation) === currentUserDocumentContactId)
      .filter(annotation => getAnnotationFieldType(annotation) === DocumentFieldType.CONSENT);

    relevantAnnotations.forEach((x: Core.Annotations.SignatureWidgetAnnotation) => {
      x.ReadOnly = false;
      x.fieldFlags.set('ReadOnly', false);
    });
  }

  /**
   * Re-evaluates annotation visibility after switching the active signing contact.
   * Called from the view-engagement-container when user changes the dropdown.
   */
  reEvaluateAnnotationsForActiveContact(): void {
    const instance = this.pdfViewerInstance$.value;

    if (!instance) {
      return;
    }

    const annotations = instance.Core.annotationManager.getAnnotationsList();

    // Reset annotation state
    annotations.forEach(annotation => {
      annotation.Hidden = false;
      annotation.ReadOnly = false;
      annotation.Listable = true;
    });

    // Update the Apryse current user to match the active contact
    this.updateCurrentUserForActiveContact();

    // Re-run hide logic with the new active contact
    this.hideIrrelevantAnnotations(annotations);

    // Re-lock auto-populated fields that should never be user-editable
    this.setTypeFieldsReadonly(annotations, [DocumentFieldType.DATE, DocumentFieldType.NAME_AND_TITLE]);

    // Rebuild navigable annotations for the new active contact
    const filteredAnnotations = this.filterAdditionalAnnotationsByStep(annotations);
    this.navigableAnnotations = this.getNavigableAnnotations(filteredAnnotations);
    this.currentFieldIndex = null;

    // Redraw
    instance.Core.annotationManager.drawAnnotationsFromList(annotations);
  }

  /**
   * Updates the Apryse annotationManager's current user to match the active signing contact.
   * This controls what name appears in the "Type" tab of the signature creation tool.
   */
  private updateCurrentUserForActiveContact(): void {
    const instance = this.pdfViewerInstance$.value;

    if (!instance) {
      return;
    }

    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);
    
    if (!isMultiIdentity) {
      return;
    }

    const activeContactId = this.store.selectSnapshot(EngagementsState.getActiveSigningContactId);
    const profiles = this.store.selectSnapshot(EngagementsState.getSigningContactProfiles);
    const activeProfile = profiles.find(profile => profile.id === activeContactId);

    if (activeProfile?.contactInfo?.fullName) {
      instance.Core.annotationManager.setCurrentUser(activeProfile.contactInfo.fullName);
    }
  }

  /**
   * Computes a composite cache key for a documentContactId. The key is "contactId#positionInGroup"
   * where positionInGroup is this mapping's index among all mappings sharing the same contactId
   * in the same document (sorted by mapping id). This keeps separate cache slots for a single
   * person signing multiple times on one document, while still matching the same position across
   * documents (letter → consent) for cross-step restore.
   */
  private cacheKeyForDocContactId(docContactId: number): string {
    const multiState = this.store.selectSnapshot(EngagementsState.getMultipleIdentitySigningState);
    const mappings = multiState?.documentContactMappings ?? [];
    const target = mappings.find(mapping => mapping.id === docContactId);

    if (!target) {
      return `${docContactId}`;
    }

    const sameGroup = mappings
      .filter(mapping => mapping.engagementDocumentId === target.engagementDocumentId && mapping.contactId === target.contactId)
      .sort((a, b) => a.id - b.id);
    const position = sameGroup.findIndex(mapping => mapping.id === docContactId);

    return `${target.contactId}#${position}`;
  }

  /**
   * Saves the current contact's drawn signature/initials to the per-contact cache,
   * then restores the target contact's cached signature (if any) into the signature tool.
   * If the target contact has no cached signature, the tool is cleared so the modal shows fresh.
   */
  switchSavedSignatures(fromDocContactId: number, toDocContactId: number): void {
    const instance = this.pdfViewerInstance$.value;

    if (!instance) {
      return;
    }

    const signatureTool = instance.Core.documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;
    this.persistToolStateToContactCache(fromDocContactId);

    while (signatureTool.getSavedSignatures?.()?.length > 0) {
      signatureTool.deleteSavedSignature(0);
    }

    while (signatureTool.getSavedInitials?.()?.length > 0) {
      signatureTool.deleteSavedInitials(0);
    }

    const cached = AbstractApryseStep._savedSignaturesPerContact.get(this.cacheKeyForDocContactId(toDocContactId));

    if (cached) {
      cached.signatures.forEach((signature: FreeHandAnnotation | StampAnnotation) => signatureTool.saveSignatures?.(signature));
      cached.initials.forEach((initial: FreeHandAnnotation | StampAnnotation) => signatureTool.saveInitials?.(initial));
    }
  }

  /**
   * Wipes Apryse's in-memory saved signatures/initials and the per-contact cache when the
   * logged-in auth user has changed since the last instance acquisition. Without this,
   * signing as contact A on a secure link, then logging in as contact B on the same tab,
   * surfaces A's drawn signature in the signature modal because the singleton WebViewer
   * (and our static cache) outlive the auth session.
   */
  private clearSignatureCachesIfUserChanged(instance: WebViewerInstance): void {
    const currentUserId = this.authService.getUserSnapshot()?.getUserId();

    if (AbstractApryseStep._lastSignatureToolUserId === currentUserId) {
      return;
    }

    const signatureTool = instance.Core.documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;

    while (signatureTool.getSavedSignatures?.()?.length > 0) {
      signatureTool.deleteSavedSignature(0);
    }

    while (signatureTool.getSavedInitials?.()?.length > 0) {
      signatureTool.deleteSavedInitials(0);
    }

    AbstractApryseStep._savedSignaturesPerContact.clear();
    AbstractApryseStep._lastSignatureToolUserId = currentUserId;
  }

  /**
   * Wipes the per-contact signature cache and Apryse's in-memory saved signatures/initials
   * when the active engagement changes. The cache is intentionally preserved between
   * documents within the same engagement (letter → consent → addon, where the same person
   * signs the same way) but must NOT bleed across engagements.
   */
  private clearSignatureCachesIfEngagementChanged(instance: WebViewerInstance): void {
    const currentEngagementId = this.store.selectSnapshot(EngagementsState.getSelectedEngagementId);

    if (currentEngagementId == null || AbstractApryseStep._lastSignatureToolEngagementId === currentEngagementId) {
      AbstractApryseStep._lastSignatureToolEngagementId = currentEngagementId;
      return;
    }

    const signatureTool = instance.Core.documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;

    while (signatureTool.getSavedSignatures?.()?.length > 0) {
      signatureTool.deleteSavedSignature(0);
    }

    while (signatureTool.getSavedInitials?.()?.length > 0) {
      signatureTool.deleteSavedInitials(0);
    }

    AbstractApryseStep._savedSignaturesPerContact.clear();
    AbstractApryseStep._lastSignatureToolEngagementId = currentEngagementId;
  }

  /** Reset the tool's saved signatures to a specific contact's cache so locationSelected applies the right sig. */
  private syncSignatureToolToContact(docContactId: number): void {
    const instance = this.pdfViewerInstance$.value;

    if (!instance) {
      return;
    }

    const signatureTool = instance.Core.documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;

    while (signatureTool.getSavedSignatures?.()?.length > 0) {
      signatureTool.deleteSavedSignature(0);
    }

    while (signatureTool.getSavedInitials?.()?.length > 0) {
      signatureTool.deleteSavedInitials(0);
    }

    const cached = AbstractApryseStep._savedSignaturesPerContact.get(this.cacheKeyForDocContactId(docContactId));

    if (!cached) {
      return;
    }

    cached.signatures.forEach((signature: FreeHandAnnotation | StampAnnotation) => signatureTool.saveSignatures?.(signature));
    cached.initials.forEach((initial: FreeHandAnnotation | StampAnnotation) => signatureTool.saveInitials?.(initial));
  }

  private static readonly OWNER_CONTACT_KEY = 'aiwyn:ownerDocContactId';

  /** Save only signatures that belong to the active contact — never stamp another contact's annotation. */
  private persistToolStateToContactCache(docContactId: number): void {
    const instance = this.pdfViewerInstance$.value;

    if (!instance) {
      return;
    }

    const signatureTool = instance.Core.documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;
    const signatures = signatureTool.getSavedSignatures?.() ?? [];
    const initials = signatureTool.getSavedInitials?.() ?? [];
    const expectedAuthor = instance.Core.annotationManager.getCurrentUser?.() ?? '';
    const ownerKey = String(docContactId);

    const belongsToContact = (annotation: any): boolean => {
      if (!annotation) {
        return false;
      }

      const existingOwner = annotation.getCustomData?.(AbstractApryseStep.OWNER_CONTACT_KEY);
      
      if (existingOwner) {
        return existingOwner === ownerKey;
      }
      
      return !!expectedAuthor && annotation.Author === expectedAuthor;
    };

    const ownSignatures = signatures.filter(belongsToContact);
    const ownInitials = initials.filter(belongsToContact);
    const lastSignature = ownSignatures.length > 0 ? ownSignatures[ownSignatures.length - 1] : undefined;
    const lastInitial = ownInitials.length > 0 ? ownInitials[ownInitials.length - 1] : undefined;

    if (!lastSignature && !lastInitial) {
      return;
    }

    lastSignature?.setCustomData?.(AbstractApryseStep.OWNER_CONTACT_KEY, ownerKey);
    lastInitial?.setCustomData?.(AbstractApryseStep.OWNER_CONTACT_KEY, ownerKey);

    AbstractApryseStep._savedSignaturesPerContact.set(this.cacheKeyForDocContactId(docContactId), {
      signatures: lastSignature ? [lastSignature] : [],
      initials: lastInitial ? [lastInitial] : [],
    });
  }

  resizeEditableSignatureAnnotations(annotations: Annotation[]): void {
    const isSelectedEngagementDocumentEdited = this.store.selectSnapshot(EngagementsState.isSelectedEngagementDocumentEdited(this.getCurrentDocumentFileId()));

    if (isSelectedEngagementDocumentEdited) {
      return;
    }

    const documentInfo = this.getCurrentDocumentInfo();
    const isCurrentUserSignatory = this.store.selectSnapshot(EngagementsState.isUserSignatoryOnSelectedEngagement);

    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);
    
    const allUserDocContactIds = isMultiIdentity
      ? this.store.selectSnapshot(EngagementsState.getAllUserDocumentContactIds(documentInfo.documentId))
      : [];

    const editableSignatureAnnotations = annotations
      .filter(annotation =>
        [DocumentFieldType.SIGNATURE, DocumentFieldType.INITIAL].includes(getAnnotationFieldType(annotation)))
      .filter(
        documentInfo.signatureRequirementType === SignatureRequirementType.ONE && ![EngagementStepType.CONSENT, EngagementStepType.ADD_ON].includes(this.details().type)
          ? _ => isCurrentUserSignatory
          : signatureAnnotation => {
              const annotDocContactId = getAnnotationDocumentContactId(signatureAnnotation);
              
              // Resize all user contacts' widgets, not just the primary one
              return annotDocContactId === documentInfo.documentContactId
                || (isMultiIdentity && allUserDocContactIds.includes(annotDocContactId));
            },
      );

    editableSignatureAnnotations.forEach(signatureAnnotation => {
      const [x1, y1, x2, y2] = this.getResizedSignatureAnnotationCoordinates(signatureAnnotation);
      signatureAnnotation.resize(new this.pdfViewerInstance$.value.Core.Math.Rect(x1, y1, x2, y2));
    });
  }

  adjustTextFieldPositions(annotations: Annotation[]): void {
    const isSelectedEngagementDocumentEdited = this.store.selectSnapshot(EngagementsState.isSelectedEngagementDocumentEdited(this.getCurrentDocumentFileId()));
    if (isSelectedEngagementDocumentEdited) {
      return;
    }

    const fields = this.getFields();
    const textFieldTypes = [
      DocumentFieldType.DATE,
      DocumentFieldType.NAME_AND_TITLE,
    ];
    const textFields = annotations
      .map(annotation => findFieldByName(fields, getAnnotationFieldName(annotation)))
      .filter(matchingField => matchingField != null && textFieldTypes.includes(getFieldType(matchingField.name)));

    setTimeout(() => {
      textFields.forEach(({ widgets }) => {
        widgets.forEach(widget => {
          const { x1, y1, x2, y2 } = widget.getRect();
          widget.setRect(new this.pdfViewerInstance$.value.Core.Math.Rect(x1, y1 + 2, x2, y2 + 2));
        });
      });
    }, 0);
  }

  setTypeFieldsReadonly(annotations: Annotation[], fieldTypes: DocumentFieldType[], readOnly: boolean = true): void {
    const fields = this.getFields();
    const matchingFields = annotations
      .map(annotation => getAnnotationFieldName(annotation))
      .filter(annotationFieldName => fieldTypes.includes(getFieldType(annotationFieldName)))
      .map(annotationFieldName => findFieldByName(fields, annotationFieldName));

    matchingFields.forEach(({ widgets }) => widgets.forEach(widget => {
      widget.fieldFlags.set('ReadOnly', readOnly);
      widget.ReadOnly = readOnly;
    }));
  }

  getNavigableAnnotations(annotations: Annotation[]): NavigableAnnotation[] {
    if (annotations == null || annotations.length === 0) {
      return this.navigableAnnotations;
    }

    const fields = this.getFields();

    const relevantAnnotations: Annotation[] = this.filterAdditionalAnnotationsByStep(
      annotations.filter(({ Subject }) => Subject !== AnnotationSubject.SIGNATURE)
    );

    const newNavigableAnnotations = relevantAnnotations.reduce(
      (acc, annotation) => {
        const fieldType = getAnnotationFieldType(annotation);
        if ([DocumentFieldType.SIGNATURE, DocumentFieldType.INITIAL].includes(fieldType)) { // && annotationDocumentContactId === currentUserDocumentContactId) {
          return acc.concat({
            type: AnnotationType.SIGNATURE,
            index: (this.navigableAnnotations?.length ?? 0) + acc.length + 1,
            data: annotation,
            isSigned: false,
          });
        }
        if (DocumentFieldType.NAME_AND_TITLE === fieldType) {
          const matchingField = findFieldByName(fields, getAnnotationFieldName(annotation));

          return acc.concat({
            type: AnnotationType.TEXT,
            index: (this.navigableAnnotations?.length ?? 0) + acc.length + 1,
            data: annotation,
            populated: !!matchingField.value,
          });
        }

        if (DocumentFieldType.CONSENT === fieldType) {
          // Group consent radio buttons per contact so each contact gets their own
          // navigable entry and consent dialog. Without this, all contacts' consent
          // fields collapse into a single RADIO entry and only the first triggers.
          const annotationDocContactId = getAnnotationDocumentContactId(annotation);
          const index = acc.findIndex(annot => annot.type === AnnotationType.RADIO && getAnnotationDocumentContactId(annot.data) === annotationDocContactId);
          const radioAnnotation = acc[index] as RadioAnnotation;

          const newAnnotation: NavigableAnnotation = {
            type: AnnotationType.RADIO,
            index: (this.navigableAnnotations?.length ?? 0) + acc.length + 1,
            data: annotation,
            linkedTo: [],
          };

          return index !== -1
            ? [
              ...acc.slice(0, index),
              { ...radioAnnotation, linkedTo: [...radioAnnotation.linkedTo, annotation] },
              ...acc.slice(index + 1)
            ]
            : acc.concat(newAnnotation);
        }

        return acc;
      },
      this.navigableAnnotations,
    );

    const navigableAnnotationFieldNames: string[] = this.navigableAnnotations.map(({ data }) => getAnnotationFieldName(data));
    const merged = this.navigableAnnotations.concat(
      newNavigableAnnotations.filter(({ data }) => !navigableAnnotationFieldNames.includes(getAnnotationFieldName(data))),
    );

    // Sort by visual position (page number, then top-to-bottom within each page).
    // Apryse fires annotationChanged per page and getAnnotationsList() returns annotations
    // in PDF form field definition order, not spatial order. Without sorting, navigation
    // order can be unpredictable (e.g., signatures before their associated consent fields).
    return merged.sort((fieldA, fieldB) => {
      const pageDiff = fieldA.data.getPageNumber() - fieldB.data.getPageNumber();
      
      if (pageDiff !== 0) {
        return pageDiff;
      }

      return fieldA.data.getY() - fieldB.data.getY();
    });
  }

  getMatchingSignatureWidgetAnnotation(signature: Annotation): Annotation {
    const signatureAnnotations = this.pdfViewerInstance$.value.Core.annotationManager.getAnnotationsList()
      .filter((annotation): annotation is Core.Annotations.SignatureWidgetAnnotation =>
        annotation instanceof this.pdfViewerInstance$.value.Core.Annotations.SignatureWidgetAnnotation);

    const associatedWidget = signatureAnnotations.find((annot) => annot.getCustomData(this.ASSOCIATED_SIGNATURE_ID) === signature.Id);

    if (associatedWidget) {
      return associatedWidget;
    }

    // When multi-identity is active, restrict proximity matching to only the active contact's widgets.
    // Without this, a signature placed on contact B's widget could match to contact A's nearby widget.
    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);
    const activeDocContactId = isMultiIdentity ? this.getCurrentUserDocumentContactId() : null;
    
    const candidateWidgets = activeDocContactId
      ? signatureAnnotations.filter(annotation => getAnnotationDocumentContactId(annotation) === activeDocContactId)
      : signatureAnnotations;

    // DO NOT USE https://docs.apryse.com/api/web/Core.Annotations.SignatureWidgetAnnotation.html#getAssociatedSignatureAnnotation__anchor,
    // it does not correctly calculate the signature coordinates to the correct widget and will most times return null
    return candidateWidgets
      .filter(annotation => annotation.getPageNumber() === signature.getPageNumber())
      .reduce(
        (closestSignatureAnnotation, currentSignatureAnnotation) => {
          if (closestSignatureAnnotation == null) {
            return currentSignatureAnnotation;
          }

          const signatureY = signature.getY();
          const signatureX = signature.getX();
          const closestDist = Math.sqrt(
            Math.pow(Math.abs(closestSignatureAnnotation.getY() - signatureY), 2)
            + Math.pow(Math.abs(closestSignatureAnnotation.getX() - signatureX), 2),
          );
          const currentDist = Math.sqrt(
            Math.pow(Math.abs(currentSignatureAnnotation.getY() - signatureY), 2)
            + Math.pow(Math.abs(currentSignatureAnnotation.getX() - signatureX), 2),
          );

          return closestDist < currentDist ? closestSignatureAnnotation : currentSignatureAnnotation;
        },
        null as Annotation,
      );
  }

  getCurrentUserDocumentContactId(): number {
    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);
    
    if (isMultiIdentity) {
      const documentInfo = this.getCurrentDocumentInfo();
      const activeContactId = this.store.selectSnapshot(EngagementsState.getActiveSigningContactId);
      
      const mapping = this.store.selectSnapshot(EngagementsState.getDocumentContactMapping(documentInfo.documentId, activeContactId));
      
      if (mapping) {
        return mapping.id;
      }
    }
    return this.getCurrentDocumentInfo().documentContactId;
  }

  getCurrentDocumentFileId(): number {
    return this.getCurrentDocumentInfo().file.id;
  }

  /**
   * Gets the document info for the current step, resolving by documentId first, then falling back to step type
   */
  protected getCurrentDocumentInfo(): DocumentInfo {
    const engagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const stepDocumentId = (this.details().tasks?.find(task => task.documentId))?.documentId;
    const fallbackType = STEP_LABEL_DOCUMENT_TYPE_MAPPING[this.details().type];

    return stepDocumentId
      ? engagement.documentsInfoById?.[stepDocumentId] ?? engagement.documentsInfo[fallbackType]
      : engagement.documentsInfo[fallbackType];
  }

  setCustomAnnotationStyles(): void {
    if (!this.details().isReadonly) {
      this.pdfViewerInstance$.value.Core.Annotations.WidgetAnnotation['getCustomStyles'] =
        (widget: WidgetAnnotation) => this.getCustomWidgetStyles(widget);
    }

    this.pdfViewerInstance$.value.Core.Annotations.WidgetAnnotation['getContainerCustomStyles'] =
      (widget: WidgetAnnotation) => {
        if (widget instanceof this.pdfViewerInstance$.value.Core.Annotations.RadioButtonWidgetAnnotation) {
          return {
            // map-get($gray-palette, '800')
            border: '1px solid #CCCFD7',
            'border-radius': '50%',
          };
        }
      };

    // Sign-here buttons use fixed-px dimensions scaled via the `--apryse-scale` CSS var,
    // applied per wrapper at creation. On zoom changes we walk every signature widget and
    // refresh the var so the buttons follow the new zoom (Apryse doesn't re-call the
    // create-sign-here handler on zoom).
    this._addInstanceEventHandler(
      this.pdfViewerInstance$.value.Core.documentViewer,
      'zoomUpdated',
      () => this.updateSignHereButtonScales(),
    );

    this.pdfViewerInstance$.value.Core.Annotations.setCustomCreateSignHereElementHandler(
      (_tool, { annotation }) => {
        const sigWidget = annotation as Core.Annotations.SignatureWidgetAnnotation;
        const fieldType = getAnnotationFieldType(annotation);
        const documentContactId = getAnnotationDocumentContactId(annotation);
        const currentUserDocumentContactId = this.getCurrentUserDocumentContactId();
        const isCurrentUser = documentContactId === currentUserDocumentContactId;

        const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);
        
        const belongsToAnyUserContact = isMultiIdentity
          ? this.store.selectSnapshot(EngagementsState.getAllUserDocumentContactIds(this.getCurrentDocumentInfo().documentId)).includes(documentContactId)
          : false;

        // Helper to create hidden element
        const createHiddenElement = (): HTMLDivElement => {
          const emptyElement = document.createElement('div');
          emptyElement.style.display = 'none';
          return emptyElement;
        };

        const isAlreadySigned = this.isSignatureAnnotationSigned(sigWidget);

        this.logger.info('ENGAGEMENTS : AbstractApryseStep :: Tool', { fieldType, documentContactId, currentUserDocumentContactId, isCurrentUser, isAlreadySigned, readOnly: this.details().isReadonly });

        if (this.details().isReadonly) {
          return createHiddenElement();
        }

        if (!isCurrentUser && !belongsToAnyUserContact) {
          return createHiddenElement();
        }

        // Build the sign-here element
        const isSignature = fieldType === DocumentFieldType.SIGNATURE;
        const element = document.createElement('div');
        // Sign-here buttons inside `element` use `calc(var(--apryse-scale) * Npx)` for every
        // dimension. Apryse's WebViewer 11 widget container is sized in pixels (rect * zoom)
        // without a CSS transform, so children with fixed-px dimensions don't auto-scale on
        // mobile. Setting the CSS variable on the wrapper here makes every child rule
        // resolve at the correct size; updateSignHereButtonScales() updates it on zoom.
        this.applySignHereScale(element);

        if (isAlreadySigned) {
          return createHiddenElement();
        }

        const isWidgetDisabled = () => sigWidget.ReadOnly === true
          || this.pdfViewerInstance$.value?.Core.annotationManager.isReadOnlyModeEnabled() === true
          || this._isSubmitting;

        // Legacy flow gating is on the multi-identity mode itself, NOT on isMultiIdentity. The
        // isMultiIdentity selector requires both the mode ON and contactProfiles.length >= 2,
        // so a mode-on / single-contact engagement would otherwise fall through to the
        // legacy red button when it should render the modern "Sign as" / "Initials" UI.
        const multiIdentityFlagEnabled = this.store.selectSnapshot(EngagementsState.getSelectedEngagement)?.multipleIdentityEnabled
          ?? this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_ENGAGEMENT_MULTIPLE_IDENTITY));

        if (!multiIdentityFlagEnabled) {
          element.innerHTML = getLegacySignHereButtonHTML(isSignature);
          
          (sigWidget as any)._signHereElement = element;
          
          // Apryse's built-in click wiring breaks on recycled instances (PP-3606), triggering manually instead
          element.addEventListener('click', (event) => {
            event.stopPropagation();
            
            if (isWidgetDisabled()) {
              this.applyDisabledStateToElement(element, isSignature);
              return;
            }
            
            this.resetContactSigningIfInitialsMismatch(sigWidget);
            this.pdfViewerInstance$.value?.UI?.signSignatureWidget?.(sigWidget);
          });
          return element;
        }

        const actionLabel = isSignature ? 'Sign as' : 'Initials';

        let contactName = '';
        let contactEmail = '';
        let initials = '';

        const multiState = this.store.selectSnapshot(EngagementsState.getMultipleIdentitySigningState);
        const mapping = multiState?.documentContactMappings?.find(entry => entry.id === documentContactId);
        const profile = mapping ? multiState?.contactProfiles?.find(entry => entry.id === mapping.contactId) : undefined;

        if (profile?.contactInfo) {
          contactName = profile.contactInfo.fullName ?? '';
          contactEmail = profile.contactInfo.email ?? '';
          initials = getInitials(profile.contactInfo.fullName);
        } else {
          // Single-contact, FF-on case: multipleIdentitySigningState is never populated
          // (LoadMultipleIdentitySigningData requires >= 2 profiles), so fall back to the
          // logged-in auth user so the button still surfaces name/email/initials instead
          // of showing a bare "Sign as" / "Initials" label.
          const user = this.authService.getUserSnapshot();
          contactName = user?.getDisplayName() ?? '';
          contactEmail = user?.getProviderEmail() ?? '';
          initials = getInitials(contactName);
        }

        const hasContactInfo = !!contactName;

        element.innerHTML = isSignature
          ? getSignButtonHTML({ actionLabel, contactName, contactEmail, hasContactInfo: !!hasContactInfo, disabled: isWidgetDisabled() })
          : getInitialsButtonHTML({ actionLabel, initials, disabled: isWidgetDisabled() });

        // Store a reference on the widget so we can hide this element after signing
        (sigWidget as any)._signHereElement = element;

        // For multi-identity: take FULL control of every click. Prevent Apryse's default
        // handling entirely so it never auto-applies cached signatures from another contact.
        // We programmatically call signSignatureWidget for every click.
        if (isMultiIdentity && belongsToAnyUserContact) {
          // Multi-identity: take full control of every click with confirmation when switching contacts.
          // This replaces the fallback handler below — both call signSignatureWidget, so only one should be registered.
          element.addEventListener('click', (event) => {
            event.stopPropagation();
            event.preventDefault();

            // Block signing when the widget has become readonly (e.g., after submit).
            // The widget's ReadOnly flag can flip AFTER this element was rendered, so we
            // re-check at click time rather than trusting the render-time snapshot.
            if (isWidgetDisabled()) {
              this.applyDisabledStateToElement(element, isSignature);
              return;
            }

            const currentActiveDocContactId = this.getCurrentUserDocumentContactId();
            const needsSwitch = documentContactId !== currentActiveDocContactId;

            const proceedWithSigning = () => {
              if (needsSwitch) {
                const targetContactId = this.store.selectSnapshot(EngagementsState.getContactIdByDocumentContactId(documentContactId));

                if (targetContactId) {
                  this.store.dispatch(new SetActiveSigningContact(targetContactId));
                  this.updateCurrentUserForActiveContact();
                  // switchSavedSignatures handled by view-engagement-container's reactive subscription.
                }
              }

              this.resetContactSigningIfInitialsMismatch(sigWidget);
              setTimeout(() => {
                // Sync the tool to the widget's contact so locationSelected can't pick up a leftover from another contact.
                this.syncSignatureToolToContact(documentContactId);
                this.pdfViewerInstance$.value.UI.signSignatureWidget(sigWidget);
              }, 0);
            };

            // If switching to a different contact, confirm with the user first
            if (needsSwitch) {
              const multiState = this.store.selectSnapshot(EngagementsState.getMultipleIdentitySigningState);
              const targetMapping = multiState?.documentContactMappings?.find(entry => entry.id === documentContactId);
              const targetProfile = targetMapping ? multiState?.contactProfiles?.find(entry => entry.id === targetMapping.contactId) : undefined;
              const { fullName, email } = targetProfile?.contactInfo ?? {};

              // The dialog renders message via [innerHTML]="message | safeHtml" which calls
              // bypassSecurityTrustHtml, so any user-controlled value MUST be HTML-escaped
              // before interpolation to prevent stored XSS from a malicious contact name/email.
              const safeFullName = escapeHtml(fullName ?? '');
              const safeEmail = escapeHtml(email ?? '');

              const engagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
              const contactManagerHtml = getContactManagerHTML(engagement?.managerName, engagement?.managerEmail);

              this.dialog
                .open(ConfirmationModalComponent, {
                  data: {
                    title: `Sign as ${safeFullName}?`,
                    message: `
                      This signature block is intended for <strong>${safeFullName}</strong> to sign.
                      <br />
                      <br />
                      Since this identity is also linked to this same email: <strong>${safeEmail}</strong>, would you want to change your identity to <strong>${safeFullName}</strong> and sign?
                      <br />
                      <br />
                      If you think ${safeFullName} should not be linked to your email, ${contactManagerHtml}
                    `,
                    confirmButtonText: 'Sign Now',
                    confirmButtonColor: 'primary',
                    textAlign: 'left',
                  },
                  backdropClass: 'confirmation-dialog-backdrop',
                })
                .afterClosed()
                .pipe(takeUntil(this._unsubscribe$))
                .subscribe(confirmed => {
                  if (confirmed) {
                    proceedWithSigning();
                  }
                });
            } else {
              proceedWithSigning();
            }
          });
        } else {
          // Apryse's built-in click wiring breaks on recycled instances (PP-3606), triggering manually instead
          element.addEventListener('click', (event) => {
            event.stopPropagation();
            if (isWidgetDisabled()) {
              this.applyDisabledStateToElement(element, isSignature);
              return;
            }
            this.resetContactSigningIfInitialsMismatch(sigWidget);
            this.pdfViewerInstance$.value?.UI?.signSignatureWidget?.(sigWidget);
          });
        }

        return element;
      },
    );
  }

  /**
   * Overrides the default pixel-level hit-testing for StampAnnotation and FreeHandAnnotation
   * so that clicking anywhere within the signature's bounding box selects it,
   * not just on the drawn pixels.
   */
  enableBoundingBoxSelectionForSignatures(): void {
    const { Annotations } = this.pdfViewerInstance$.value.Core;
    const { SelectionModel, SelectionAlgorithm } = Annotations;

    const selectionModelsToOverride = [
      Annotations.StampAnnotation.prototype.selectionModel,
      Annotations.FreeHandAnnotation.prototype.selectionModel,
    ];

    for (const selectionModel of selectionModelsToOverride) {
      if (!selectionModel) {
        continue;
      }

      // Note: Apryse's type definition for CustomTestSelectionHandler omits the `annotation`
      // first parameter, but the runtime API (and their own docs example) passes it.
      // We use `any` to match the actual runtime signature.
      SelectionModel.setCustomHandlers(selectionModel, {
        testSelection(
          annotation: any, x: number, y: number, pageMatrix: Core.Math.Matrix, zoom: number, rotation: Core.PageRotation,
          { originalTestSelection }: any,
        ) {
          if (annotation?.Subject === AnnotationSubject.SIGNATURE) {
            return SelectionAlgorithm.boundingRectTest(annotation, x, y, zoom);
          }
          
          return originalTestSelection(annotation, x, y, pageMatrix, zoom, rotation);
        },
      } as any);
    }
  }

  setReadonlyView(annotations: Annotation[]): void {
    annotations.forEach(annotation => {
      // In WebViewer 11, signatures are committed to the widget as an appearance
      // So we need to show signature widgets that have an appearance (they contain the signed signature)
      const isSignatureWidget = annotation.Subject === AnnotationSubject.WIDGET
        && annotation instanceof this.pdfViewerInstance$.value.Core.Annotations.SignatureWidgetAnnotation;

      // Check if signature widget has an appearance (could be under Normal, _DEFAULT, or other keys)
      let hasSignatureAppearance = false;
      if (isSignatureWidget) {
        const sigWidget = annotation as Core.Annotations.SignatureWidgetAnnotation;
        const appearances = sigWidget.appearances;
        // Check for any appearance key that contains signature data
        hasSignatureAppearance = appearances && Object.keys(appearances).length > 0;

        // Make the signature widget fully readonly - disable clicking/interaction
        sigWidget.ReadOnly = true;
        sigWidget.Locked = true;
        sigWidget.fieldFlags?.set('ReadOnly', true);
      }

      annotation.Hidden = annotation.Subject !== AnnotationSubject.SIGNATURE
        && !annotation['value']
        && !hasSignatureAppearance;
      annotation.Listable = false;
      annotation.ReadOnly = true;
    });

    this.getFields().forEach(field => field.widgets.forEach(widget => widget.fieldFlags.set('ReadOnly', true)));
  }

  navigateToField(direction: 'previous' | 'next'): void {
    // Ignore text based annotations that are part of signature block, this won't impact validation, since filled annotations
    // aren't removed from the navigableAnnotations list
    const filteredNavigableAnnotations = this.navigableAnnotations.filter(annot => annot.type !== AnnotationType.TEXT);

    // First interaction edge case
    if (this.currentFieldIndex == null) {
      this.currentFieldIndex = direction === 'previous' ? filteredNavigableAnnotations.length - 1 : 0;
    } else {
      // Modular Arithmetic
      this.currentFieldIndex = (
        direction === 'previous'
          ? (this.currentFieldIndex + (filteredNavigableAnnotations.length - 1))
          : (this.currentFieldIndex + 1)
      ) % filteredNavigableAnnotations.length;
    }


    const annotation = filteredNavigableAnnotations[this.currentFieldIndex];
    const documentFieldType = getAnnotationFieldType(annotation.data)

    this.pdfViewerInstance$.value.Core.annotationManager.jumpToAnnotation(annotation.data);

    if (documentFieldType === DocumentFieldType.CONSENT && annotation.type === AnnotationType.RADIO) {
      this.handleConsentDialog(annotation);
    }
  }

  getCustomWidgetStyles(widget: WidgetAnnotation): { [style: string]: string } {
    const fieldType = getAnnotationFieldType(widget);

    if ([DocumentFieldType.DATE, DocumentFieldType.NAME_AND_TITLE].includes(fieldType)) {
      return { 'cursor': 'default' };
    }

    // Strip Apryse's readonly grey overlay on signature/initial widgets — our injected
    // sign-here button already conveys the disabled state, and the grey box underneath
    // looks out of place.
    const isSignatureLikeField = [DocumentFieldType.SIGNATURE, DocumentFieldType.INITIAL].includes(fieldType);
    const transparentBackground: { [style: string]: string } = isSignatureLikeField ? { 'background-color': 'transparent' } : {};

    if (
      [DocumentFieldType.SIGNATURE, DocumentFieldType.INITIAL, DocumentFieldType.CONSENT].includes(fieldType) &&
      this.getCurrentUserDocumentContactId() === getAnnotationDocumentContactId(widget)
    ) {
      return { ...transparentBackground, 'cursor': 'pointer' };
    }

    return transparentBackground;
  }

  getSignatures(): Annotation[] {
    const annotations = this.pdfViewerInstance$.value.Core.annotationManager.getAnnotationsList();
    return annotations.filter(({ Subject }) => Subject === AnnotationSubject.SIGNATURE);
  }

  getFields(): Field[] {
    return this.pdfViewerInstance$.value?.Core.annotationManager.getFieldManager().getFields();
  }

  // Utility methods have been moved to apryse-utils.ts

  getOutOfDateFields(): Field[] {
    return this.getFields().filter(field => field.value && field.widgets[0].value !== field.value);
  }

  updateOutOfDateFieldValues(outOfDateFields: Field[]): void {
    outOfDateFields.forEach(outOfDateField => outOfDateField.setValue(outOfDateField.widgets[0].value));
  }

  getResizedSignatureAnnotationCoordinates(signatureAnnotation: Annotation): RectCoordinates {
    const { x1, y1 } = signatureAnnotation.getRect();
    return getAnnotationFieldType(signatureAnnotation) === DocumentFieldType.SIGNATURE
      ? [x1, y1, x1 + 300, y1 + 30]
      : [x1, y1 + 10, x1 + 80, y1 + 30];
  }

  updateNavigableFieldsValidity(): void {
    const navigableFields = this.navigableAnnotations
      .filter<TextAnnotation>((annotation): annotation is TextAnnotation => annotation.type === AnnotationType.TEXT);
    const outOfDateFields = navigableFields.filter(({ populated, data }) => populated !== !!data['value']);
    outOfDateFields.forEach(field => field.populated = !!field.data['value']);

    const signatures = this.getSignatures();
    const matchingSignatureAnnotations = signatures
      .map(signature => this.getMatchingSignatureWidgetAnnotation(signature))
      .map(signatureAnnotation => {
        const annotationFieldName = getAnnotationFieldName(signatureAnnotation);
        return this.navigableAnnotations.find(({ data }) => getAnnotationFieldName(data) === annotationFieldName);
      })
      .filter(annotation => annotation != null) as SignatureAnnotation[];

    const matchingSignatureAnnotationFieldNames = matchingSignatureAnnotations.map(({ data }) => getAnnotationFieldName(data));
    // Only mark signatures as signed if we found matching signatures.
    matchingSignatureAnnotations.forEach(annotation => annotation.isSigned = true);

    // Check all signature annotations and determine signed status
    const allSignatureAnnotations = this.navigableAnnotations
      .filter(({ type }) => type === AnnotationType.SIGNATURE) as SignatureAnnotation[];

    for (const annotation of allSignatureAnnotations) {
      const fieldName = getAnnotationFieldName(annotation.data);

      if (matchingSignatureAnnotationFieldNames.includes(fieldName)) {
        // Already marked as signed above
        continue;
      }

      // Check if the widget has an associated signature or has been signed
      // In WebViewer 11, signatures can be "committed" to the widget as an appearance
      const widget = annotation.data as Core.Annotations.SignatureWidgetAnnotation;
      const hasAssociatedSignature = widget.getAssociatedSignatureAnnotation?.() != null;
      const hasCustomDataSignature = !!widget.getCustomData?.(this.ASSOCIATED_SIGNATURE_ID);
      // Check if the widget has an appearance (signature was committed/merged into widget)
      // Appearance can be under Normal, _DEFAULT, or other keys
      // For typed signatures, appearances might still exist after deletion if not properly cleared
      // We check if there are any appearance keys with meaningful content
      const hasAppearance = widget.appearances &&
        Object.keys(widget.appearances).length > 0 &&
        // Additional check: ensure at least one appearance has meaningful content
        // If clearSignature() was called, appearances should be empty or all values null/empty
        Object.values(widget.appearances).some((appearance: any) => {
          // Appearance can be various types, so we check for truthiness
          // null, undefined, empty strings should be considered "no appearance"
          if (appearance == null || appearance === undefined) {
            return false;
          }
          // For strings, check if non-empty
          if (typeof appearance === 'string') {
            return appearance.length > 0;
          }
          // For objects, check if they have properties
          if (typeof appearance === 'object') {
            return Object.keys(appearance).length > 0;
          }
          // For other types (numbers, booleans, etc.), consider them as valid appearances
          return true;
        });

      // Always update isSigned based on the actual widget state
      // This ensures that when a signature is deleted, isSigned is properly set to false
      if (hasAssociatedSignature || hasCustomDataSignature || hasAppearance) {
        annotation.isSigned = true;
      } else {
        // Set to false if widget is not signed (including when signature was deleted)
        annotation.isSigned = false;
      }
    }
  }

  getAnnotationsForCurrentDocumentContact(): Annotation[] {
    const annotations = this.pdfViewerInstance$.value.Core.annotationManager.getAnnotationsList();
    const documentInfo = this.getCurrentDocumentInfo();

    if (documentInfo.signatureRequirementType === SignatureRequirementType.ONE) {
      return annotations;
    } else {
      const currentUserIdentifier = this.getCurrentUserDocumentContactId();
      return annotations.filter(annotation => {
        const annotationDocumentContactIdentifier = getAnnotationDocumentContactId(annotation);
        return annotationDocumentContactIdentifier === currentUserIdentifier;
      });
    }
  }

  getFieldsForCurrentDocumentContact(): Field[] {
    const fields = this.getFields();
    const documentInfo = this.getCurrentDocumentInfo();

    if (documentInfo.signatureRequirementType === SignatureRequirementType.ONE) {
      return fields;
    } else {
      const currentUserIdentifier = this.getCurrentUserDocumentContactId();
      return fields.filter(field => {
        const baseFieldName = `Engagement.DocumentContact.${currentUserIdentifier}.`;
        return field.name.startsWith(baseFieldName);
      });
    }
  }

  /**
   * This method sets every annotation and field associated with the current document contact to readonly mode.
   * This should only occur when the PDF is absolutely ready for submission.
   */
  setReadOnlyMode(): void {
    const annotations = this.getAnnotationsForCurrentDocumentContact();
    const fields = this.getFieldsForCurrentDocumentContact();
    annotations.forEach(annotation => {
      annotation.ReadOnly = true;
    });
    fields.forEach(field => {
      field.widgets.forEach(widget =>
        widget.fieldFlags.set('ReadOnly', true)
      );
    });
    /**
     * This should be removed if this blocks the same user from signing > 1 documents within the same session.
     */
    this.pdfViewerInstance$.value.Core.annotationManager.enableReadOnlyMode();

    // After flipping widgets to readonly, update the injected sign-here elements so their
    // visual state (grey background, not-allowed cursor) matches Apryse's own readonly overlay.
    this.refreshDisabledStateForAllSignHereElements();
  }

  onFieldsUpdated(fieldValues: Record<string, string | number>): void {
    this._fieldValues$.next(fieldValues);
  }

  /**
   * Filter tasks to the current user, scoped to every contact the user is linked to (sibling / multi-identity).
   * Already-completed tasks are excluded so a cosigner's finished task can no longer vacuously pass validateOptionalTask, while still allowing the user's outstanding tasks to be found.
   */
  getTasksForUser(): EngagementTask[] {
    const userContactIds = this.store.selectSnapshot(EngagementsState.readUserContactIds);

    return this.details().tasks
      .filter((task) =>
        ENGAGEMENT_STEP_TASKS[this.details().type].taskTypes.includes(task.type)
        && userContactIds.includes(task.contactId)
        && !isTaskCompleted(task.status)
      ).sort((a, b) => {
        if (a.status === EngagementTaskStatus.REQUIRED) {
          return 1;
        }

        if (b.status === EngagementTaskStatus.REQUIRED) {
          return -1;
        }

        return 1;
      });
  }

  /**
   * Get incomplete annotations for the user
   */
  getIncompleteAnnotations(): NavigableAnnotation[] {
    return this.navigableAnnotations.filter(annotation => {
      const isUnsignedSignatureAnnotation = annotation.type === AnnotationType.SIGNATURE && !(annotation as SignatureAnnotation).isSigned;
      const isUnpopulatedAnnotations = (annotation.type === AnnotationType.TEXT)
        && getAnnotationFieldType(annotation.data) === DocumentFieldType.NAME_AND_TITLE
        && !annotation.populated;
      const isRadioUnselected = annotation.type === AnnotationType.RADIO
        && annotation.data['value'] === ConsentValues.OFF
        && (annotation.linkedTo.length > 0 && annotation.linkedTo.every(annot => annot['value'] === ConsentValues.OFF));

      return isUnsignedSignatureAnnotation || isUnpopulatedAnnotations || isRadioUnselected;
    })
  }

  /**
   * validate that the required task. By default, checks that annotations are complete.
   * @param task
   */
  validateRequiredTask(task: EngagementTask): boolean {
    return this.getIncompleteAnnotations().length === 0;
  }

  /**
   * validate that the optional task. By default, checks that all or none of the annotations have been completed.
   * @param task
   */
  validateOptionalTask(task: EngagementTask): boolean {
    return [0, this.navigableAnnotations.length].includes(this.getIncompleteAnnotations().length);
  }

  /**
   * When multiple identity signing is active, validates that ALL user contacts have signed their fields.
   * If the active contact is complete but another contact has incomplete fields, auto-switches to that contact.
   * Returns an error result if any contact is incomplete, or null if all are valid.
   */
  private validateAllIdentitiesBeforeSubmit(): EngagementStepSubmissionResult | null {
    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);
    
    if (!isMultiIdentity) {
      return null;
    }

    // First check if the active contact has incomplete annotations -- let normal flow handle that
    if (this.getIncompleteAnnotations().length > 0) {
      return null;
    }

    // Active contact is complete -- check other contacts
    const documentInfo = this.getCurrentDocumentInfo();
    const multiState = this.store.selectSnapshot(EngagementsState.getMultipleIdentitySigningState);

    if (!multiState) {
      return null;
    }

    const activeContactId = multiState.activeSigningContactId;
    const instance = this.pdfViewerInstance$.value;

    if (!instance) {
      return null;
    }

    const allAnnotations = instance.Core.annotationManager.getAnnotationsList();

    for (const profile of multiState.contactProfiles) {
      if (profile.id === activeContactId) {
        continue;
      }

      const mapping = multiState.documentContactMappings.find(mapping => mapping.engagementDocumentId === documentInfo.documentId && mapping.contactId === profile.id,);
      
      if (!mapping || mapping.signedAt) {
        continue;
      }

      // Check if this contact has unsigned signature widgets
      const contactAnnotations = allAnnotations.filter(annotation => getAnnotationDocumentContactId(annotation) === mapping.id);
      
      const hasUnsignedSignature = contactAnnotations.some(annotation => {
        const fieldType = getAnnotationFieldType(annotation);
        
        if (fieldType !== DocumentFieldType.SIGNATURE && fieldType !== DocumentFieldType.INITIAL) {
          return false;
        }

        return !this.isSignatureAnnotationSigned(annotation);
      });

      // Switch to the incomplete contact so they can see what's missing
      if (hasUnsignedSignature) {
        this.store.dispatch(new SetActiveSigningContact(profile.id));
        this.reEvaluateAnnotationsForActiveContact();

        return {
          success: false,
          errorMessage: `Please sign as ${profile.contactInfo.fullName} to continue.`,
        };
      }
    }

    return null;
  }

  /**
   * Get the action that submit should call for the given task
   * @param task
   * @param updatedEngagementLetterData
   */
  abstract getSubmitActionForTask(task: EngagementTask, updatedEngagementLetterData: string): any

  /**
   * Get the tasks that the user should be completing. Differs per step
   * @param userTasks
   */
  abstract getNecessaryTasks(userTasks: EngagementTask[]): EngagementTask[]

  /**
   * Filter additional annotations based on the step type
   * @param annotations
   */
  abstract filterAdditionalAnnotationsByStep(annotations: Annotation[]): Annotation[]

  submit(isMobile?: boolean): Observable<EngagementStepSubmissionResult> {
    // Multi-contact pre-check: if active contact is valid, check all other contacts too
    const multiIdentityValidationResult = this.validateAllIdentitiesBeforeSubmit();
    
    if (multiIdentityValidationResult) {
      return of(multiIdentityValidationResult);
    }

    // Check if the user has any tasks for this step
    const userTasks: EngagementTask[] = this.getNecessaryTasks(this.getTasksForUser());
    if (!userTasks.length) {
      return throwError(new Error('Task not found for the user on this step.'));
    }

    // Validate the users tasks based on if they are required or optional
    const validatedTasks: { task: EngagementTask, isValid: boolean }[] = userTasks.map(task => ({
      task,
      isValid: task.status === EngagementTaskStatus.REQUIRED
        ? this.validateRequiredTask(task)
        : this.validateOptionalTask(task)
    }));

    // Are there any valid tasks?
    const validTask: EngagementTask = validatedTasks.find(({ isValid }) => isValid)?.task;
    if (!validTask) {
      if (validatedTasks[0].task.status === EngagementTaskStatus.REQUIRED) {
        this.navigateToFirstIncompleteAnnotation();
      }

      // Show error message based off what missing annotation is needed
      const incompleteAnnotations = this.getIncompleteAnnotations();
      switch (getAnnotationFieldType(incompleteAnnotations[0].data)) {
        case DocumentFieldType.SIGNATURE:
          return of({ success: false, errorMessage: 'Please sign the document.' });
        case DocumentFieldType.NAME_AND_TITLE:
          return of({ success: false, errorMessage: 'Please provide your name and title or remove your signature.' });
        case DocumentFieldType.INITIAL:
          return of({ success: false, errorMessage: 'Please add initial to engagement letter or remove your name and title.' });
        case DocumentFieldType.CONSENT:
          // Find the first INCOMPLETE consent/deny so the dialog highlights the one
          // the user actually needs to fill, not one that's already been answered.
          const annotation = incompleteAnnotations.find(
            incompletAnnotation => incompletAnnotation.type === AnnotationType.RADIO
          );

          if (!annotation) {
            return  of({ success: false, errorMessage: 'Consent annotation not found.' });
          }

          this.handleConsentDialog(annotation);

          return !isMobile
            ? of({ success: false, errorMessage: 'Please provide or deny consent.' })
            : of(null);
      }
    }

    // There is a valid task, submit action based on task
    this._isSubmitting = true;

    this.logger.info('AbstractApryseStep :: Submit - Waiting for Apryse to commit annotations', { datetime: Date.now() });
    // Wait for Apryse's annotation manager to finish any pending operations
    // This ensures all annotations (including recently added signatures) are fully committed
    // before we export the PDF. Without this, rapid submit after signature can export an empty signature block.
    return from(this.pdfViewerInstance$.value.Core.annotationManager.exportAnnotations()).pipe(
      tap(() => this.logger.info('AbstractApryseStep :: Submit - Annotations committed, proceeding with export', { datetime: Date.now() })),
      // Small additional delay to ensure internal Apryse state is fully settled
      delay(100),
      switchMap(() => this.pdfViewer.first.getDocumentWithAnnotationData()),
      switchMap(updatedEngagementLetterData => this.store.dispatch(this.getSubmitActionForTask(validTask, updatedEngagementLetterData))),
      map(() => {
        this.setReadOnlyMode();
        return { success: true };
      }),
      catchError(error => {
        const errorMessage: string = error.message || '';
        const engagementId = this.store.selectSnapshot(EngagementsState.getSelectedEngagementId);

        const errorCode = `ENG${engagementId}-${validTask.type}-${generateUniqueCode()}`;

        const engagementErrorPayload: EngagementErrorStrategyPayload = { engagementId, errorMessage, errorCode };
        this.trackService.track(`Submit Engagement Error :: ${validTask.type} failed`, { ...engagementErrorPayload });

        const concreteStrategy = this.getEngagementErrorDialogStrategy(engagementErrorPayload);
        return this.dialog.open(EngagementErrorDialogComponent, {
          data: { concreteStrategy, errorCode },
        }).afterClosed().pipe(switchMap(_ => of({ success: false, errorMessage })));
      }),
      finalize(() => { this._isSubmitting = false; }),
    );
  }

  /**
   * Navigate to the first incomplete annotation.
   */
  navigateToFirstIncompleteAnnotation(): void {
    const incompleteAnnotations = this.getIncompleteAnnotations();
    const filteredNavigableAnnotation = this.navigableAnnotations.filter(annot => annot.type !== AnnotationType.TEXT);
    let index = filteredNavigableAnnotation.indexOf(incompleteAnnotations[0] as SignatureAnnotation | RadioAnnotation);
    const currentContact = this.getCurrentUserDocumentContactId();
    const signatureBlockNumber = getAnnotationSignatureBlockNumber(incompleteAnnotations[0].data);
    const signatureField = `Engagement.DocumentContact.${currentContact}.${DocumentFieldType.SIGNATURE}_${signatureBlockNumber}`;
    const signatureWidget = this.pdfViewerInstance$.value.Core.annotationManager.getAnnotationsList()
      .find(annot => getAnnotationFieldName(annot) === signatureField);

    // index will be -1 for unfilled text fields
    index = index === -1
      ? filteredNavigableAnnotation.findIndex(annot => annot.data === signatureWidget)
      : index;
    this.currentFieldIndex = index;
    this.pdfViewerInstance$.value.Core.annotationManager.jumpToAnnotation(incompleteAnnotations[0].data);
  }

  getEngagementErrorDialogStrategy(payload: EngagementErrorStrategyPayload): EngagementErrorDialogDataModel {
    this.logger.error('ENGAGEMENTS :: AbstractApryseStep :: getEngagementErrorDialogStrategy :: Error', { ...payload });

    const { engagementId, errorMessage, errorCode } = payload;
    const hasAdditionalTasks = this.store.selectSnapshot(EngagementsState.getSelectedEngagementSteps).length > 1;

    const errorStrategies: Record<string, EngagementErrorDialogDataModel> = {
      'the document you tried to sign is out of date': new EngagementDocumentAlteredBeforeSigningStrategyModel(engagementId),
      'revision': new DocumentUnderRevisionStrategyModel(engagementId),
      'your signature is no longer needed on this document': new UserNoLongerSignatoryStrategyModel(engagementId, hasAdditionalTasks),
      'document has already been signed by someone else': new DocumentSignedBySomeoneElseStrategyModel(hasAdditionalTasks, engagementId),
      'engagement has already been executed': new EngagementAlreadyExecutedStrategyModel(engagementId),
      'engagement has been deleted': new EngagementDeletedStrategyModel(engagementId),
      'Consent is required': new EngagementConsentRequiredStrategyModel(engagementId),
    };

    const foundStrategy = Object.entries(errorStrategies).find(([errorSubstring]) => errorMessage.includes(errorSubstring));

    if (foundStrategy) {
      const [_, strategyModel] = foundStrategy;
      return strategyModel;
    }

    return new EngagementGenericErrorStrategyModel(engagementId, errorCode);
  }

  ngOnDestroy(): void {
    // Save the active contact's signatures before the step component is destroyed,
    // so they're available when the next step (or this step again) initializes.
    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);
    
    if (isMultiIdentity) {
      const instance = this.pdfViewerInstance$.value;

      if (instance) {
        const signatureTool = instance.Core.documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;
        const currentSignatures = signatureTool.getSavedSignatures?.() ?? [];
        const currentInitials = signatureTool.getSavedInitials?.() ?? [];
        
        if (currentSignatures.length > 0 || currentInitials.length > 0) {
          this.persistToolStateToContactCache(this.getCurrentUserDocumentContactId());
        }
      }
    }

    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this._removeAllInstanceEventHandlers();
    this._resetInstanceFeatures();
  }

  private _addInstanceEventHandler(target: any, event: string, handler: (...args: any[]) => void): void {
    target.addEventListener(event, handler);
    this._instanceEventHandlers.push({ target, event, handler });
  }

  private _removeAllInstanceEventHandlers(): void {
    for (const { target, event, handler } of this._instanceEventHandlers) {
      try { target.removeEventListener(event, handler); } catch { /* ignore */ }
    }
    this._instanceEventHandlers.length = 0;
  }

  /** Reset UI features that were conditionally enabled for this step so they don't leak into the next step. */
  private _resetInstanceFeatures(): void {
    const instance = this.pdfViewerInstance$.value;
    if (!instance) {
      return;
    }
    try {
      instance.UI.disableFeatures([instance.UI.Feature.Initials]);
    } catch { /* ignore — instance may already be disposed */ }
  }

  private handleConsentDialog(annotation: NavigableAnnotation): void {
    const userTasks: EngagementTask[] = this.getNecessaryTasks(this.getTasksForUser());

    const isRequired = userTasks.find(task => task.type === EngagementTaskType.PROVIDE_CONSENT)?.status === EngagementTaskStatus.REQUIRED;
    const field = (annotation.data as WidgetAnnotation).getField();

    this.store.select(EngagementsState.getSelectedEngagementConsentSettings).pipe(
      switchMap(settings => {
        if (settings) {
          return this.dialog.open(ProvideOrDenyConsentDialogComponent, {
            data: { initialValue: field.getValue(), isRequired, settings }
          }).afterClosed();
        }
        console.error('Error retrieving consent settings.');
        return EMPTY;
      }),
      filter(response => !!response),
      switchMap(response => {
        if (field) {
          field.setValue(response);
          return from(this.pdfViewerInstance$.value.Core.annotationManager.drawAnnotations({ pageNumber: annotation.data.getPageNumber() }));
        }

        return of(null);
      }),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  /**
   * Baseline zoom the sign-here buttons were designed for. The engagement viewer opens at
   * this zoom by default, so scale=1 here keeps the buttons at their original visual size
   * and only smaller zooms (e.g., mobile fit-to-width) cause them to shrink.
   */
  private static readonly SIGN_BUTTON_DESIGN_ZOOM = 1.25;

  private getSignHereScale(): number {
    const zoom = this.pdfViewerInstance$.value?.Core?.documentViewer?.getZoomLevel?.() ?? 1;
    return zoom / AbstractApryseStep.SIGN_BUTTON_DESIGN_ZOOM;
  }

  /**
   * Sets the `--apryse-scale` CSS variable on a sign-here wrapper. The injected button HTML
   * uses `calc(var(--apryse-scale) * Npx)` for every dimension, so updating this single
   * property re-sizes the entire button without rebuilding innerHTML.
   */
  private applySignHereScale(element: HTMLElement, scale: number = this.getSignHereScale()): void {
    element.style.setProperty('--apryse-scale', String(scale));
  }

  /**
   * Walks every signature widget and refreshes the `--apryse-scale` on its sign-here
   * wrapper. Called from the `zoomUpdated` listener so buttons track zoom changes live.
   */
  private updateSignHereButtonScales(): void {
    const instance = this.pdfViewerInstance$.value;
    if (!instance) {
      return;
    }

    const scale = this.getSignHereScale();
    const widgets = instance.Core.annotationManager.getAnnotationsList()
      .filter((annotation): annotation is Core.Annotations.SignatureWidgetAnnotation =>
        annotation instanceof instance.Core.Annotations.SignatureWidgetAnnotation);

    for (const widget of widgets) {
      const element = (widget as any)._signHereElement as HTMLElement | undefined;
      if (element) {
        this.applySignHereScale(element, scale);
      }
    }
  }

  /**
   * Adds the disabled modifier class to the injected sign-here element so it visually
   * matches Apryse's readonly state. Handles both the signature and initials variants.
   */
  private applyDisabledStateToElement(element: HTMLElement, isSignature: boolean): void {
    const target = element.querySelector<HTMLElement>(isSignature ? '.sign-button' : '.initials-button');
    if (!target) {
      return;
    }

    target.classList.add(isSignature ? 'sign-button-disabled' : 'initials-button-disabled');
  }

  /**
   * Applies the disabled state to every injected sign-here element whose widget has
   * been marked readonly. Safe to call after state changes (e.g., after submit) so the
   * custom buttons visually mirror Apryse's readonly overlay.
   */
  private refreshDisabledStateForAllSignHereElements(): void {
    const instance = this.pdfViewerInstance$.value;
    if (!instance) {
      return;
    }

    const widgets = instance.Core.annotationManager.getAnnotationsList()
      .filter((annotation): annotation is Core.Annotations.SignatureWidgetAnnotation =>
        annotation instanceof instance.Core.Annotations.SignatureWidgetAnnotation);
    const readOnlyModeOn = instance.Core.annotationManager.isReadOnlyModeEnabled();

    for (const widget of widgets) {
      const element = (widget as any)._signHereElement as HTMLElement | undefined;
      if (!element) {
        continue;
      }
      if (widget.ReadOnly === true || readOnlyModeOn) {
        const isSignature = getAnnotationFieldType(widget) === DocumentFieldType.SIGNATURE;
        this.applyDisabledStateToElement(element, isSignature);
      }
    }
  }

  /**
   * Apryse can't pair a cached signature with freshly-drawn initials — the "Create" modal
   * validates its internal mouse-tracked state, so any pre-filled signature canvas is
   * ignored. When the current widget requires initials but we only have a cached signature
   * (no cached initials), clear everything signed so far for this contact on the current
   * document and reset the cache. The user then provides both signature and initials in
   * the modal, like a fresh first sign — no mixed state, no stuck "Create" button.
   */
  private resetContactSigningIfInitialsMismatch(sigWidget: Core.Annotations.SignatureWidgetAnnotation): void {
    const instance = this.pdfViewerInstance$.value;
    if (!instance) {
      return;
    }

    if (!sigWidget.requiresInitials?.()) {
      return;
    }

    const signatureTool = instance.Core.documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;
    const hasCachedSignature = (signatureTool.getSavedSignatures?.()?.length ?? 0) > 0;
    const hasCachedInitial = (signatureTool.getSavedInitials?.()?.length ?? 0) > 0;

    if (!hasCachedSignature || hasCachedInitial) {
      return;
    }

    const documentContactId = getAnnotationDocumentContactId(sigWidget);
    this.clearContactSignaturesAndCache(documentContactId);
  }

  /**
   * Removes all signature/initial annotations for the given contact on the current document,
   * clears the signature tool's saved signatures and initials, and evicts the contact's cache
   * entry. Shared by the edit flow and the "missing initials" reset.
   */
  private clearContactSignaturesAndCache(documentContactId: number): void {
    const instance = this.pdfViewerInstance$.value;
    if (!instance) {
      return;
    }

    const signatureTool = instance.Core.documentViewer.getTool('AnnotationCreateSignature') as Core.Tools.SignatureCreateTool;
    const allAnnotations = instance.Core.annotationManager.getAnnotationsList();

    const contactAnnotationsToDelete = allAnnotations.filter(annotation => {
      if (annotation.Subject !== AnnotationSubject.SIGNATURE) {
        return false;
      }

      const matchingWidget = this.getMatchingSignatureWidgetAnnotation(annotation);
      return matchingWidget && getAnnotationDocumentContactId(matchingWidget) === documentContactId;
    });

    for (const annotation of contactAnnotationsToDelete) {
      const widget = this.getMatchingSignatureWidgetAnnotation(annotation) as Core.Annotations.SignatureWidgetAnnotation;
      if (widget) {
        this.tryClearWidgetSignature(widget, instance.Core.annotationManager);
      }
    }

    instance.Core.annotationManager.deleteAnnotations(contactAnnotationsToDelete, { force: true });

    while (signatureTool.getSavedSignatures?.()?.length > 0) {
      signatureTool.deleteSavedSignature(0);
    }
    while (signatureTool.getSavedInitials?.()?.length > 0) {
      signatureTool.deleteSavedInitials(0);
    }

    AbstractApryseStep._savedSignaturesPerContact.delete(this.cacheKeyForDocContactId(documentContactId));
  }

  private tryClearWidgetSignature(
    widget: Core.Annotations.SignatureWidgetAnnotation,
    annotationManager: Core.AnnotationManager,
  ): void {
    try {
      widget.clearSignature(annotationManager);
    } catch (error) {
      this.logger.warn('ENGAGEMENTS : AbstractApryseStep :: Could not clear widget signature', error);
    }
  }
}
